import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { Calculator, History, DollarSign, Percent, TrendingUp, Wallet, Award,
         ChevronLeft, Fuel, Ruler, FlaskConical, Settings, Hash, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInAppPurchase } from './hooks/useInAppPurchase';
import { useAdMob, financialMinutesLeft } from './hooks/useAdMob';
import { RemoveAdsModal } from './components/RemoveAdsModal';
// --- NEW FIREBASE IMPORTS ---
import { initializeApp, getApps, getApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { 
  getAuth,
  signInWithPopup, 
  GoogleAuthProvider, 
  onAuthStateChanged, 
  signOut 
} from "firebase/auth";
// --- FIREBASE SETUP ---
const firebaseConfig = {
  apiKey: "AIzaSyAfozgMy7ZjSQ2odEqbaAGQ8SCTFpjD_hM",
  authDomain: "calc-360-d3099.firebaseapp.com",
  projectId: "calc-360-d3099",
  storageBucket: "calc-360-d3099.firebasestorage.app",
  messagingSenderId: "804319972484",
  appId: "1:804319972484:web:f5b8312607a4c3db0f06d1",
  measurementId: "G-M3QRPVTJ9Z"
};

// Guard against duplicate initialization during hot reload
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
const auth = getAuth(app);


// FIXED: Wrap Analytics in a try/catch. If an ad blocker kills it, the app survives!
let analytics = null;
try {
  analytics = getAnalytics(app);
} catch (e) {
  console.warn("Analytics blocked by browser extension.");
}



// Detect Android web browsers (to redirect them to Play Store)
const isAndroidWeb = () => {
  if (typeof window === 'undefined') return false;
  
  // 1. Check if the device is Android
  const isAndroid = /android/i.test(window.navigator.userAgent);
  
  // 2. Check if it is running securely inside our native Capacitor wrapper
  // (Capacitor returns 'web' if they are just using Chrome/Edge)
  const isNativeApp = window.Capacitor && window.Capacitor.getPlatform() !== 'web';
  
  // 3. If they are on Android but NOT in the native app, block them!
  return isAndroid && !isNativeApp;
};

// Safe backspace icon — Delete may not exist in lucide-react@0.263.1
const BackspaceIcon = ({ size }) => (
  <span style={{ fontSize: size ? size * 0.72 + 'px' : '16px', lineHeight: 1, userSelect: 'none' }}>⌫</span>
);

const FONT = "'SamsungOne', 'Samsung Sharp Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const isCapacitor = () => {
  if (typeof window === 'undefined' || !window.Capacitor) return false;
  return window.Capacitor.getPlatform() !== 'web';
};

// Native Tooltip Dictionary (Complete)
const TOOLTIPS = {
  '()': 'Brackets', '%': 'Percentage', '÷': 'Division', '×': 'Multiplication',
  '−': 'Subtraction', '+': 'Addition', '=': 'Equals', 'C': 'Clear', 'AC': 'All clear',
  
  // Angle Modes
  'Deg': 'Degrees', 'Rad': 'Radians', '⇋': 'Swap',

  // Primary Scientific
  'sin': 'Sine', 'cos': 'Cosine', 'tan': 'Tangent', 
  'ln': 'Natural logarithm', 'log': 'Logarithm', 'eˣ': 'e to the power of x',
  'x²': 'x squared', 'xʸ': 'x to the power of y', '1/x': 'Inverse number',
  '√': 'Square root', '|x|': 'Absolute value', 'π': 'Pi', 'e': 'Euler\'s number',

  // Secondary Scientific
  'sin⁻¹': 'Inverse sine', 'cos⁻¹': 'Inverse cosine', 'tan⁻¹': 'Inverse tangent',
  'sinh': 'Hyperbolic sine', 'cosh': 'Hyperbolic cosine', 'tanh': 'Hyperbolic tangent',
  'sinh⁻¹': 'Inverse hyperbolic sine', 'cosh⁻¹': 'Inverse hyperbolic cosine', 'tanh⁻¹': 'Inverse hyperbolic tangent',
  'x³': 'x cubed', '³√': 'Cube root', '2ˣ': '2 to the power of x', 'x!': 'Factorial'
};


const UNIT_SYMBOLS = {
  meter:'m', kilometer:'km', centimeter:'cm', millimeter:'mm', mile:'mi', yard:'yd', foot:'ft', inch:'in',
  'nautical mile':'NM', mil:'mil',
  kilogram:'kg', gram:'g', milligram:'mg', 'metric ton':'t', 'UK ton':'t(UK)', 'US ton':'t(US)', pound:'lb', ounce:'oz',
  celsius:'°C', fahrenheit:'°F', kelvin:'K',
  liter:'L', milliliter:'mL', 'uk gallon':'gal(UK)', 'us gallon':'gal(US)', gallon:'gal', quart:'qt', pint:'pt', cup:'cup', 'fluid ounce':'fl oz',
  'cubic meter':'m³', 'cubic centimeter':'cm³', 'cubic foot':'ft³', 'cubic inch':'in³',
  'square meter':'m²', 'square kilometer':'km²', 'square mile':'mi²', acre:'ac', hectare:'ha',
  'are':'a', 'square centimeter':'cm²', 'square foot':'ft²', 'square inch':'in²',
  'meter/second':'m/s', 'meter/hour':'m/h', 'kilometer/second':'km/s', 'kilometer/hour':'km/h',
  'mile/second':'mi/s', 'mile/hour':'mph', knot:'kn',
  'foot/second':'ft/s', 'foot/hour':'ft/h', 'inch/second':'in/s', 'inch/hour':'in/h',
  second:'s', millisecond:'ms', minute:'min', hour:'h', day:'d', week:'wk', month:'mo', year:'yr',
  bit:'bit', byte:'B', kilobyte:'KB', kibibyte:'KiB', megabyte:'MB', mebibyte:'MiB',
  gigabyte:'GB', gibibyte:'GiB', terabyte:'TB', tebibyte:'TiB',
  joule:'J', kilojoule:'kJ', calorie:'cal', kilocalorie:'kcal', 'watt-hour':'Wh',
  pascal:'Pa', kilopascal:'kPa', bar:'bar', psi:'psi', atmosphere:'atm',
  degree:'°', radian:'rad', gradian:'grad',
  watt:'W', kilowatt:'kW', horsepower:'hp',
  hertz:'Hz', kilohertz:'kHz', megahertz:'MHz',
};

// Exchange rates vs USD — last fetched March 2026
const POPULAR_CURRENCIES = [
  ['USD','US Dollar'],['EUR','Euro'],['GBP','British Pound'],['INR','Indian Rupee'],
  ['JPY','Japanese Yen'],['CNY','Chinese Yuan'],['AED','UAE Dirham'],['SGD','Singapore Dollar'],
  ['CAD','Canadian Dollar'],['AUD','Australian Dollar'],['CHF','Swiss Franc'],
  ['HKD','Hong Kong Dollar'],['SEK','Swedish Krona'],['NOK','Norwegian Krone'],
  ['NZD','New Zealand Dollar'],['ZAR','South African Rand'],['BRL','Brazilian Real'],
  ['MXN','Mexican Peso'],['KRW','South Korean Won'],['THB','Thai Baht'],
  ['MYR','Malaysian Ringgit'],['IDR','Indonesian Rupiah'],['PHP','Philippine Peso'],
  ['PKR','Pakistani Rupee'],['BDT','Bangladeshi Taka'],['SAR','Saudi Riyal'],
  ['KWD','Kuwaiti Dinar'],['QAR','Qatari Riyal'],['OMR','Omani Rial'],
  ['TRY','Turkish Lira'],['RUB','Russian Ruble'],['NGN','Nigerian Naira'],
];

// FX rates are fetched live daily — see useFxRates hook below

// ─── Expression formatter with bidirectional cursor mapping ───────────────────
// Formats each number token in an expression string with locale commas,
// and returns functions to map cursor positions between the raw and formatted strings.
function buildFormattedExpression(expr, locale) {
  if (!expr) return { formatted: '', rawToFmt: p => p, fmtToRaw: p => p };

  let formatted = '';
  const rawToFmtArr = []; // rawToFmtArr[rawIdx] → fmtIdx
  const fmtToRawArr = []; // fmtToRawArr[fmtIdx] → rawIdx

  let ri = 0;
  while (ri < expr.length) {
    if (/\d/.test(expr[ri])) {
      // Collect whole number token (digits + optional decimal point)
      let rj = ri;
      while (rj < expr.length && /[\d.]/.test(expr[rj])) rj++;
      const numStr   = expr.slice(ri, rj);
      const dotIdx   = numStr.indexOf('.');
      const intPart  = dotIdx === -1 ? numStr : numStr.slice(0, dotIdx);
      const decPart  = dotIdx === -1 ? ''     : numStr.slice(dotIdx); // includes '.'
      const intVal   = parseInt(intPart, 10);
      const fmtInt   = isNaN(intVal) ? intPart : intVal.toLocaleString(locale);
      const fmtNum   = fmtInt + decPart;
      const fmtStart = formatted.length;

      // Map each raw integer digit to its position in fmtInt (skipping inserted commas)
      let rawIntPos = 0, fmtIntPos = 0;
      while (rawIntPos < intPart.length) {
        // Commas in fmtInt map to the next raw digit's position
        while (fmtIntPos < fmtInt.length && !/\d/.test(fmtInt[fmtIntPos])) {
          fmtToRawArr.push(ri + rawIntPos);
          fmtIntPos++;
        }
        rawToFmtArr.push(fmtStart + fmtIntPos);
        fmtToRawArr.push(ri + rawIntPos);
        rawIntPos++;
        fmtIntPos++;
      }

      // Map decimal part (including '.') 1-to-1
      for (let k = 0; k < decPart.length; k++) {
        rawToFmtArr.push(fmtStart + fmtInt.length + k);
        fmtToRawArr.push(ri + intPart.length + k);
      }

      formatted += fmtNum;
      ri = rj;
    } else {
      // Operator, space, paren — pass through 1-to-1
      rawToFmtArr.push(formatted.length);
      fmtToRawArr.push(ri);
      formatted += expr[ri];
      ri++;
    }
  }
  // Sentinel end entries
  rawToFmtArr.push(formatted.length);
  fmtToRawArr.push(expr.length);

  const clamp = (p, arr) => arr[Math.max(0, Math.min(p, arr.length - 1))];
  return {
    formatted,
    rawToFmt: p => clamp(p, rawToFmtArr),
    fmtToRaw: p => clamp(p, fmtToRawArr),
  };
}

// ─── Visual expression: add spaces around parens + keep fmtToVis/visToFmt ─────
function buildVisualExpression(fmtExpr) {
  if (!fmtExpr) return { visual: '', fmtToVis: p => p, visToFmt: p => p };
  let visual = '';
  const fmtToVisArr = [];
  const visToFmtArr = [];
    
  const pushMapped = (fmtIdx, ch) => {
    fmtToVisArr.push(visual.length);
    visToFmtArr.push(fmtIdx);
    visual += ch;
  };
  const pushPad = (fmtIdx) => {   // insert a display-only space, mapping it to adjacent fmt pos
    visToFmtArr.push(fmtIdx);
    visual += ' ';
  };

  for (let i = 0; i < fmtExpr.length; i++) {
    const ch = fmtExpr[i];
    if (ch === '(' || ch === ')') {
      // Leading space (only if not already preceded by a space)
      if (visual.length > 0 && visual[visual.length - 1] !== ' ') pushPad(i);
      pushMapped(i, ch);
      // Trailing space (only if not already followed by a space or another ')')
      if (i + 1 < fmtExpr.length && fmtExpr[i + 1] !== ' ' && fmtExpr[i + 1] !== ')') pushPad(i + 1);
    } else {
      pushMapped(i, ch);
    }
  }
  // Sentinel end entries
  fmtToVisArr.push(visual.length);
  visToFmtArr.push(fmtExpr.length);

  const clamp = (p, arr) => arr[Math.max(0, Math.min(p, arr.length - 1))];
  return {
    visual,
    fmtToVis: p => clamp(p, fmtToVisArr),
    visToFmt: p => clamp(p, visToFmtArr),
  };
}

// ─── Render expression as colored spans with a blinking cursor ─────────────────
const OP_CHARS = new Set(['(', ')', '+', '-', '×', '÷', '%', '^']);
// A space flanking an operator shares op-color so the gap looks part of the operator
function isOpSpace(visual, idx) {
  if (visual[idx] !== ' ') return false;
  const prev = idx > 0 ? visual[idx - 1] : '';
  const next = idx < visual.length - 1 ? visual[idx + 1] : '';
  return OP_CHARS.has(prev) || OP_CHARS.has(next);
}

function renderColoredExpr(visual, cursorVisPos, exprColor, opColor, accentColor) {
  const els = [];
  let i = 0;
  while (i <= visual.length) {
    if (i === cursorVisPos) {
      els.push(
        <span key={`caret-${i}`} data-cursor="1" style={{
          display: 'inline-block', width: 0, position: 'relative', height: '1em'
        }}>
           <span style={{
             position: 'absolute', left: 0, bottom: '-10%', height: '1.2em', width: '2px',
             backgroundColor: accentColor, animation: 'cvBlink 1s infinite', userSelect: 'none'
           }} />
        </span>
      );
    }
    if (i >= visual.length) break;

    const isOp = OP_CHARS.has(visual[i]) || isOpSpace(visual, i);
    let j = i;

    while (j < visual.length) {
      const jIsOp = OP_CHARS.has(visual[j]) || isOpSpace(visual, j);
      if (jIsOp !== isOp) break;
      if (j === cursorVisPos && j !== i) break;
      j++;
    }
    // 1. Inject a break opportunity right BEFORE operators
    if (isOp && i > 0) {
      els.push(<wbr key={`wbr-${i}`} />);
    }

    // 2. Add whiteSpace: 'nowrap' to keep numbers glued together
    els.push(
      <span key={i} style={{ color: isOp ? opColor : exprColor, whiteSpace: 'nowrap' }}>
        {visual.slice(i, j)}
      </span>
    );
    i = j;
  }
  return els;
}

const InputField = ({ label, value, onChange, placeholder, unit, colors }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom:'14px' }}>
      <label style={{ display:'block', fontSize:'0.75rem', marginBottom:'4px', fontWeight:500, color:colors.textSecondary, fontFamily:FONT }}>{label}</label>
      <div style={{ position:'relative' }}>
        <input
          type="text" inputMode="decimal" pattern="[0-9]*" value={value}
          onChange={e => { const v = e.target.value; if (v===''||v==='-'||/^-?\d*\.?\d*$/.test(v)) onChange(v); }}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          placeholder={placeholder||''}
          style={{ width:'100%', padding:'12px', paddingRight:unit?'56px':'12px', borderRadius:'12px', outline:'none', fontWeight:500,
            backgroundColor:colors.inputBg, color:colors.text, border:`1.5px solid ${focused?colors.accent:colors.border}`,
            caretColor:colors.accent, transition:'border-color 0.2s cubic-bezier(0.4,0,0.2,1)', fontFamily:FONT, boxSizing:'border-box', fontSize:'1rem' }}
        />
        {unit && <span style={{ position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)', color:colors.accent,
          fontSize:'0.7rem', fontWeight:700, pointerEvents:'none', userSelect:'none', background:colors.tagBg,
          padding:'2px 6px', borderRadius:'8px', fontFamily:FONT }}>{unit}</span>}
      </div>
    </div>
  );
};

const ResultCard = ({ data, colors }) => (
  <div style={{ padding:'16px', borderRadius:'16px', marginTop:'16px', backgroundColor:colors.inputBg, border:`1px solid ${colors.border}` }}>
    {data.map((item, i) => (
      <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
        paddingBottom:i<data.length-1?'12px':'0', marginBottom:i<data.length-1?'12px':'0',
        borderBottom:i<data.length-1?`1px solid ${colors.border}`:'none' }}>
        <span style={{ color:colors.textSecondary, fontFamily:FONT, fontSize:'0.875rem', fontWeight:500 }}>{item.label}</span>
        <span style={{ color:item.highlight?colors.accent:colors.text, fontFamily:FONT,
          fontSize:item.highlight?'1.35rem':'1rem', fontWeight:item.highlight?700:500 }}>{item.value}</span>
      </div>
    ))}
  </div>
);

// ─── Pure helpers (module-level — no state dependency) ───────────────────────

const sm = (v) => String(parseFloat(v.toPrecision(12)));
const smartStr = (val) => (!isFinite(val) || isNaN(val)) ? 'Error' : String(parseFloat(val.toPrecision(12)));

const factorial = (n) => {
  const k = Math.floor(n);
  if (k < 0 || k !== n) return NaN;
  if (k <= 1) return 1;
  let r = 1; for (let i = 2; i <= k; i++) r *= i; return r;
};

const UNITS = {
  length:      {meter:1,kilometer:0.001,centimeter:100,millimeter:1000,mile:0.000621371,yard:1.09361,foot:3.28084,inch:39.3701,'nautical mile':0.000539957,mil:39370.1},
  mass:        {kilogram:1,gram:1000,milligram:1000000,'metric ton':0.001,'UK ton':0.000984207,'US ton':0.00110231,pound:2.20462,ounce:35.274},
  temperature: {celsius:'special',fahrenheit:'special',kelvin:'special'},
  volume:      {liter:1,milliliter:1000,'cubic meter':0.001,'cubic centimeter':1000,'cubic inch':61.0237,'cubic foot':0.0353147,'uk gallon':0.219969,'us gallon':0.264172,quart:1.05669,pint:2.11338,cup:4.22675,'fluid ounce':33.814},
  area:        {'square meter':1,'square kilometer':0.000001,'square mile':3.861e-7,acre:0.000247105,hectare:0.0001,are:0.01,'square centimeter':10000,'square foot':10.7639,'square inch':1550.0031},
  speed:       {'meter/second':1,'meter/hour':3600,'kilometer/second':0.001,'kilometer/hour':3.6,'mile/second':0.000621371,'mile/hour':2.23694,knot:1.94384,'foot/second':3.28084,'foot/hour':11811.02,'inch/second':39.3701,'inch/hour':141732},
  time:        {second:1,millisecond:1000,minute:1/60,hour:1/3600,day:1/86400,week:1/604800,month:1/2629746,year:1/31556952},
  data:        {bit:1,byte:8,kilobyte:8000,kibibyte:8192,megabyte:8000000,mebibyte:8388608,gigabyte:8000000000,gibibyte:8589934592,terabyte:8000000000000,tebibyte:8796093022208},
  energy:      {joule:1,kilojoule:0.001,calorie:0.239006,kilocalorie:0.000239006,'watt-hour':0.000277778},
  pressure:    {pascal:1,kilopascal:0.001,bar:0.00001,psi:0.000145038,atmosphere:0.00000986923},
  angle:       {degree:1,radian:Math.PI/180,gradian:1.11111},
  power:       {watt:1,kilowatt:0.001,horsepower:0.00134102},
  frequency:   {hertz:1,kilohertz:0.001,megahertz:0.000001},
};

const convertTemperature = (value, from, to) => {
  let cel;
  if (from === 'celsius') cel = value;
  else if (from === 'fahrenheit') cel = (value - 32) * 5 / 9;
  else cel = value - 273.15;
  if (to === 'celsius') return cel;
  if (to === 'fahrenheit') return cel * 9 / 5 + 32;
  return cel + 273.15;
};

const convertUnit = (value, from, to, type) => {
  if (value == null || value === '' || !from || !to || !UNITS[type]) return '';
  if (from === to) return String(parseFloat(value));
  const num = parseFloat(value); if (isNaN(num)) return '';
  if (type === 'temperature') return String(parseFloat(convertTemperature(num, from, to).toPrecision(12)));
  if (!UNITS[type][from] || !UNITS[type][to]) return '';
  return String(parseFloat((num / UNITS[type][from] * UNITS[type][to]).toPrecision(12)));
};

// ─── Safe recursive-descent expression evaluator ──────────────────────────────
function evaluateExpression(expr) {
  try {
    let e = expr
      .replace(/×/g, '*').replace(/÷/g, '/').replace(/\^/g, '**').replace(/\s+/g, '');
    const open = (e.match(/\(/g) || []).length, close = (e.match(/\)/g) || []).length;
    if (open > close) e += ')'.repeat(open - close);
    e = e.replace(/(\d+\.?\d*)%/g, '($1/100)');

    let i = 0;

    function parseAddSub() {
      let left = parseMulDiv();
      while (i < e.length && (e[i] === '+' || e[i] === '-')) {
        const op = e[i++];
        left = op === '+' ? left + parseMulDiv() : left - parseMulDiv();
      }
      return left;
    }
    function parseMulDiv() {
      let left = parsePow();
      while (i < e.length && (e[i] === '*' || e[i] === '/') && e[i + 1] !== '*') {
        const op = e[i++];
        left = op === '*' ? left * parsePow() : left / parsePow();
      }
      return left;
    }
    function parsePow() {
      const base = parseUnary();
      if (i < e.length && e[i] === '*' && e[i + 1] === '*') {
        i += 2;
        return Math.pow(base, parsePow());
      }
      return base;
    }
    function parseUnary() {
      if (e[i] === '-') { i++; return -parseUnary(); }
      if (e[i] === '+') { i++; return parseUnary(); }
      return parsePrimary();
    }
    function parsePrimary() {
      if (e[i] === '(') {
        i++;
        const val = parseAddSub();
        if (e[i] === ')') i++;
        return val;
      }
      const start = i;
      while (i < e.length && /[\d.]/.test(e[i])) i++;
      if (i === start) throw new Error('Unexpected token: ' + e[i]);
      return parseFloat(e.slice(start, i));
    }

    const result = parseAddSub();
    if (typeof result !== 'number' || !isFinite(result)) return String(result);
    return String(parseFloat(result.toPrecision(12)));
  } catch (err) { console.warn('[evaluateExpression]', err); return 'Error'; }
}

// ─── Safe localStorage accessor (guards against SSR / sandboxed environments) ──
const lsGet = (key) => { try { return localStorage.getItem(key); } catch { return null; } };
const lsSet = (key, val) => { try { localStorage.setItem(key, val); } catch { /* no-op */ } };

// ─── FX Rates Hook ────────────────────────────────────────────────────────────
// Priority: live fetch → localStorage cache → hardcoded offline fallback
// Status values:
//   'loading'  – first load, no data yet
//   'live'     – freshly fetched today
//   'cached'   – using stored rates (< 24 h old, no need to refetch)
//   'stale'    – using stored rates (> 24 h, refetch attempted/failed)
//   'offline'  – no cache, falling back to built-in rates

const FX_CACHE_KEY  = 'calc_fx_cache';
const FX_TTL_MS     = 24 * 60 * 60 * 1000; // 24 hours

// Hardcoded offline fallback (USD-based, dated 08 Mar 2026)
const FX_OFFLINE_DATE  = '2026-03-08';
const FX_OFFLINE_RATES = {
  USD:1,      EUR:0.9201, GBP:0.7856, INR:86.92,  JPY:149.45, CNY:7.242,
  AED:3.6725, SGD:1.3412, CAD:1.3891, AUD:1.5673, CHF:0.8842,
  HKD:7.7802, SEK:10.412, NOK:10.691, NZD:1.7025, ZAR:18.231,
  BRL:5.831,  MXN:20.14,  KRW:1345.2, THB:34.21,  MYR:4.412,
  IDR:15832,  PHP:56.12,  PKR:278.5,  BDT:109.8,  SAR:3.7505,
  KWD:0.3071, QAR:3.641,  OMR:0.3850, TRY:36.42,  RUB:89.21,  NGN:1601,
};

const useFxRates = () => {
  // status: 'loading' | 'live' | 'cached' | 'stale' | 'offline'
  const [fxRates,  setFxRates]  = useState(null);
  const [fxDate,   setFxDate]   = useState(null);
  const [fxStatus, setFxStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;

    const apply = (rates, date, status) => {
      if (!cancelled) { setFxRates(rates); setFxDate(date); setFxStatus(status); }
    };

    const saveCache = (rates, date) =>
      lsSet(FX_CACHE_KEY, JSON.stringify({ rates, date, ts: Date.now() }));

    const fetchFresh = async (hasCachedFallback) => {
      try {
        const res  = await fetch('https://open.er-api.com/v6/latest/USD');
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const json = await res.json();
        if (json.result !== 'success') throw new Error('API error');
        const rates = json.rates;
        const date  = json.time_last_update_utc
          ? new Date(json.time_last_update_utc).toISOString().slice(0, 10)
          : new Date().toISOString().slice(0, 10);
        saveCache(rates, date);
        apply(rates, date, 'live');
      } catch (err) {
        console.warn('[FX] fetch failed:', err.message);
        // If we already showed cached data, just mark it stale — don't overwrite
        if (!cancelled && !hasCachedFallback) {
          apply(FX_OFFLINE_RATES, FX_OFFLINE_DATE, 'offline');
        } else if (!cancelled) {
          setFxStatus('stale'); // already showing cached, just update label
        }
      }
    };

    // 1. Try localStorage cache
    const raw = lsGet(FX_CACHE_KEY);
    if (raw) {
      try {
        const { rates, date, ts } = JSON.parse(raw);
        if (rates && date && typeof ts === 'number') {
          const age     = Date.now() - ts;
          const fresh   = age < FX_TTL_MS;
          apply(rates, date, fresh ? 'cached' : 'stale');
          if (!fresh) fetchFresh(true);   // silently refresh stale cache
          return () => { cancelled = true; };
        }
      } catch { /* corrupt — fall through */ }
    }

    // 2. No cache — show offline rates immediately, then try to fetch
    apply(FX_OFFLINE_RATES, FX_OFFLINE_DATE, 'offline');
    fetchFresh(false);

    return () => { cancelled = true; };
  }, []);

  return { fxRates, fxDate, fxStatus };
};

// ─── Main App ─────────────────────────────────────────────────────────────────

const CalculatorApp = ({ isProWeb, freeUsesLeft, consumeFreeUse, user, onLoginSuccess, activePasses }) => {
  // ── PWA Install State ──
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBtn, setShowInstallBtn] = useState(false);
  const [isIOSBrowser, setIsIOSBrowser] = useState(false);

  // Catch the install prompt so we can attach it to the Toolbar icon
  useEffect(() => {
    // 1. If already installed as a PWA or running as a native App, don't show the button
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (isStandalone || isCapacitor()) return;

    // 2. Detect iOS (iPhone/iPad)
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOSBrowser(ios);

    // iOS will never fire the install prompt, so we force the button to show up for them
    if (ios) {
      setShowInstallBtn(true);
    }

    // 3. Catch the magic install prompt for Chrome/Edge/Android
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault(); 
      setDeferredPrompt(e); 
      setShowInstallBtn(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'no-kbd';
    style.textContent = [
      'input[readonly] { user-select:none; -webkit-user-select:none; caret-color:transparent; }',
      'input[inputmode="none"] { caret-color:transparent; }',
    ].join('\n');
    document.head.appendChild(style);
    return () => document.getElementById('no-kbd')?.remove();
  }, []);

  const [mode, setMode]                 = useState('calculator');
  // ── Persisted settings — read from localStorage on first render ──
  const [theme, setTheme]               = useState(() => lsGet('calc_theme') ?? 'dark');
  const [numberSystem, setNumberSystem] = useState(() => lsGet('calc_numberSystem') ?? 'indian');
  const [roundingEnabled, setRoundingEnabled] = useState(() => lsGet('calc_rounding') === 'true');
  const [angleMode, setAngleMode]       = useState(() => lsGet('calc_angleMode') ?? 'deg');
  const [sciPanel, setSciPanel]         = useState(() => lsGet('calc_sciPanel') ?? 'primary');
  const [showSci, setShowSci]           = useState(false);
  const [sciVisible, setSciVisible]     = useState(false); // for CSS animation
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory]   = useState(false);
  const [showRemoveAds, setShowRemoveAds] = useState(false);
  const [expression, setExpression]     = useState('');
  const [showPremiumPaywall, setShowPremiumPaywall] = useState(false);
  const [premiumTarget, setPremiumTarget] = useState(null);
  // --- Long Press & Tooltip Logic ---
  const tooltipTimer = useRef(null);
  const isLongPress = useRef(false);
  const [btnTooltip, setBtnTooltip] = useState(null);
  const handleBtnDown = (e, label) => {
    isLongPress.current = false;
    if (navigator.vibrate) navigator.vibrate(10); // Native tap down feel

    const rect = e.currentTarget.getBoundingClientRect();
    let tx = rect.left + rect.width / 2; // Default Center
    const ty = rect.top - 8; // Position just above the finger
    
    // SMART BOUNDARIES: Prevent edge clipping
    let transformX = '-50%'; // Default centering
    if (tx < 100) {
      tx = rect.left + 8; // Anchor to the left edge of the button
      transformX = '0%';
    } else if (tx > window.innerWidth - 100) {
      tx = rect.right - 8; // Anchor to the right edge of the button
      transformX = '-100%';
    }

    // Start timer. If held for 400ms, it's a long press!
    tooltipTimer.current = setTimeout(() => {
      isLongPress.current = true;
      if (TOOLTIPS[label]) {
        // We now pass the transform alignment too!
        setBtnTooltip({ text: TOOLTIPS[label], x: tx, y: ty, txAlign: transformX });
        if (navigator.vibrate) navigator.vibrate(30); 
      }
    }, 400); 
  };

  const handleBtnUp = (fn) => {
    clearTimeout(tooltipTimer.current);
    setBtnTooltip(null);
    // ONLY execute the math if they released before the long press triggered
    if (!isLongPress.current && fn) {
      fn(); 
    }
  };

  const handleBtnCancel = () => {
    // If they slide their finger off the button, cancel everything
    clearTimeout(tooltipTimer.current);
    setBtnTooltip(null);
    isLongPress.current = false;
  };
  // -----------------------------------

  const [result, setResult]             = useState('0');
  const [lastOperation, setLastOperation] = useState(null);
  const [history, setHistory]           = useState([]);
  const [cursorPos, setCursorPos]       = useState(null);
  const [toast, setToast]               = useState(null); // { msg, id }
  const toastTimerRef = useRef(null);
  const inputRef = useRef(null);
  const fromInputRef = useRef(null);
  const toInputRef   = useRef(null);
  // cursor-restoration state for formatted converter inputs
  const cvCursorRef  = useRef({ field: null, pos: null });

  // Converter
  const [converterType, setConverterType] = useState('length');
  const [fromUnit, setFromUnit]   = useState('meter');
  const [toUnit, setToUnit]       = useState('kilometer');
  const [fromValue, setFromValue] = useState('');
  const [toValue, setToValue]     = useState('');
  const [cvActiveField, setCvActiveField] = useState('from');
  const [cvFieldSelected, setCvFieldSelected] = useState(false); // blue select-all state

  // History context menu
  const [activeHistIdx, setActiveHistIdx] = useState(null); // pressed item highlight

  // Tip calculator state
  const [tipSubtotal, setTipSubtotal]   = useState('');
  const [tipPercent, setTipPercent]     = useState(15);
  const [tipPeople, setTipPeople]       = useState(1);
  const [tipDrumOpen, setTipDrumOpen]   = useState(null); // 'tip' | 'people' | null
  const [tipDrumTemp, setTipDrumTemp]   = useState(null);

  // Live FX rates (fetched daily, cached in localStorage)
  const { fxRates, fxDate, fxStatus } = useFxRates();

  // ── Helper: Check if a feature has an active 2-minute pass ──
  const hasActivePass = useCallback((feature) => {
    if (!activePasses || !activePasses[feature]) return false;
    return Date.now() < activePasses[feature];
  }, [activePasses]);

  // ── ACCESS_NETWORK_STATE: track online/offline in real time ──
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on  = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // ── VIBRATE: haptic feedback helper (10ms tap, 30ms for equals) ──
  const vibrate = useCallback((ms = 10) => {
    try { navigator.vibrate?.(ms); } catch { /* unsupported */ }
  }, []);

  // ── WAKE_LOCK: keep screen on while app is in foreground ──
  const wakeLockRef = useRef(null);
  useEffect(() => {
    if (!('wakeLock' in navigator)) return;
    const acquire = async () => {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
      } catch { /* denied or unavailable */ }
    };
    acquire();
    const reacquire = () => { if (document.visibilityState === 'visible') acquire(); };
    document.addEventListener('visibilitychange', reacquire);
    return () => {
      document.removeEventListener('visibilitychange', reacquire);
      wakeLockRef.current?.release().catch(() => {});
    };
  }, []);

  // ── SW600dp TABLET LAYOUT: watch window width ≥ 600px ──
  const [isTablet, setIsTablet] = useState(() => window.innerWidth >= 580);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 580px)');
    const handler = (e) => setIsTablet(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Financial
  const [financialMode, setFinancialMode] = useState('emi');
  const [principal, setPrincipal] = useState(''); const [rate, setRate] = useState('');
  const [tenureYears, setTenureYears] = useState(''); const [tenureMonths, setTenureMonths] = useState(''); const [tenureDays, setTenureDays] = useState('');
  const [emiResult, setEmiResult] = useState(null);
  const [sipAmount, setSipAmount] = useState(''); const [sipRate, setSipRate] = useState(''); const [sipYears, setSipYears] = useState(''); const [sipResult, setSipResult] = useState(null);
  const [rdAmount, setRdAmount] = useState(''); const [rdRate, setRdRate] = useState(''); const [rdMonths, setRdMonths] = useState(''); const [rdResult, setRdResult] = useState(null);
  const [rdFreq, setRdFreq] = useState('monthly'); const [rdDateMode, setRdDateMode] = useState('manual'); const [rdFromDate, setRdFromDate] = useState(''); const [rdToDate, setRdToDate] = useState('');
  const [rdYears, setRdYears] = useState(''); const [rdMonthsPart, setRdMonthsPart] = useState(''); const [rdDaysPart, setRdDaysPart] = useState('');
  const [interestType, setInterestType] = useState('simple');
  const [interestPrincipal, setInterestPrincipal] = useState(''); const [interestRate, setInterestRate] = useState('');
  const [interestYears, setInterestYears] = useState(''); const [interestMonths, setInterestMonths] = useState(''); const [interestDays, setInterestDays] = useState('');
  const [compoundFrequency, setCompoundFrequency] = useState('annually'); const [interestResult, setInterestResult] = useState(null);
  const [originalPrice, setOriginalPrice] = useState(''); const [discountPercent, setDiscountPercent] = useState('');
  const [discountResult, setDiscountResult] = useState(null); const [discountMode, setDiscountMode] = useState('forward');
  const [discountFinalPrice, setDiscountFinalPrice] = useState('');
  const [costPrice, setCostPrice] = useState(''); const [sellingPrice, setSellingPrice] = useState(''); const [marginResult, setMarginResult] = useState(null);
  const [initialInvestment, setInitialInvestment] = useState(''); const [finalValue, setFinalValue] = useState(''); const [roiYears, setRoiYears] = useState(''); const [roiResult, setRoiResult] = useState(null);
  const [lastSalary, setLastSalary] = useState(''); const [yearsOfService, setYearsOfService] = useState(''); const [gratuityResult, setGratuityResult] = useState(null);
  const [distance, setDistance] = useState(''); const [fuelUsed, setFuelUsed] = useState(''); const [fuelPrice, setFuelPrice] = useState('');
  const [distanceUnit, setDistanceUnit] = useState('km'); const [fuelUnit, setFuelUnit] = useState('liter'); const [fuelEconomyResult, setFuelEconomyResult] = useState(null);
  const [gstAmount, setGstAmount] = useState(''); const [gstRate, setGstRate] = useState('');
  const [gstType, setGstType] = useState('add'); const [gstResult, setGstResult] = useState(null);
  // Date-range mode (EMI, SIP, Interest, ROI)
  const [emiDateMode, setEmiDateMode] = useState('manual');
  const [emiFromDate, setEmiFromDate] = useState('');
  const [emiToDate,   setEmiToDate]   = useState('');
  const [sipDateMode, setSipDateMode] = useState('manual');
  const [sipFromDate, setSipFromDate] = useState('');
  const [sipToDate,   setSipToDate]   = useState('');
  const [intDateMode, setIntDateMode] = useState('manual');
  const [intFromDate, setIntFromDate] = useState('');
  const [intToDate,   setIntToDate]   = useState('');
  const [roiDateMode, setRoiDateMode] = useState('manual');
  const [roiFromDate, setRoiFromDate] = useState('');
  const [roiToDate,   setRoiToDate]   = useState('');
  // ── Hooks (placed after ALL useState declarations to maintain stable hook order) ──

  // After each render, restore cursor in whichever converter field was last edited
  // Intentionally has no deps array — runs after every render to restore the
  // converter input cursor position that was stashed in cvCursorRef by the
  // last keypress/change handler. The ref resets itself to null after each use.
  useLayoutEffect(() => {
    const { field, pos } = cvCursorRef.current;
    if (pos === null) return;
    const el = field === 'from' ? fromInputRef.current : toInputRef.current;
    if (el) el.setSelectionRange(pos, pos);
    cvCursorRef.current.pos = null;
  });

  // ── Persist settings to localStorage whenever they change ──
  // Clear toast timer on unmount
  useEffect(() => { return () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }; }, []);
  useEffect(() => { lsSet('calc_theme', theme); }, [theme]);
  useEffect(() => { lsSet('calc_numberSystem', numberSystem); }, [numberSystem]);
  useEffect(() => { lsSet('calc_rounding', String(roundingEnabled)); }, [roundingEnabled]);
  useEffect(() => { lsSet('calc_angleMode', angleMode); }, [angleMode]);
  useEffect(() => { lsSet('calc_sciPanel', sciPanel); }, [sciPanel]);

  // ── In-app purchase + AdMob ──
  const { adsRemoved, price, purchasing, restoring, error: iapError, purchaseRemoveAds, restorePurchases } =
    useInAppPurchase({ onAdsRemoved: () => setShowRemoveAds(false) });
    
  // NEW: Combine native IAP 'adsRemoved' with the new 'isProWeb' from the Gatekeeper
  const isFullyPro = adsRemoved || isProWeb;
  
  const { showRewardedAd, financialUnlocked } = useAdMob({ adsRemoved: isFullyPro });

  // ── Android back button / gesture handler ──
  // Priority: settings → history → sci panel → converter/financial → exit
  useEffect(() => {
    const handleBack = () => {
      if (showSettings) { setShowSettings(false); return; }
      if (showHistory)  { setShowHistory(false);  return; }
      if (showSci) { setSciVisible(false); setTimeout(() => setShowSci(false), 320); return; }
      if (mode === 'converter' || mode === 'financial') { setMode('calculator'); return; }
      if (window.Capacitor?.Plugins?.App) { window.Capacitor.Plugins.App.exitApp(); }
    };
    let listenerHandle = null;
    const attachCapacitor = async () => {
      try {
        const { App: CapApp } = await import('@capacitor/app');
        listenerHandle = await CapApp.addListener('backButton', handleBack);
      } catch {
        window.addEventListener('popstate', handleBack);
      }
    };
    attachCapacitor();
    return () => { listenerHandle?.remove(); window.removeEventListener('popstate', handleBack); };
  }, [mode, showSettings, showHistory, showSci]);

  // ── Theme colors ── (matched from Samsung One UI screenshots)
  const dark = {
    bg:'#000000', inputBg:'#1C1C1E', btnNum:'#2B2B2D', btnFn:'#2B2B2D', btnOp:'#2B2B2D',
    btnEq:'#4A5240', btnSci:'#2B2B2D', text:'#FFFFFF', textSecondary:'#9E9E9E', exprText:'#FFFFFF',
    cRed:'#FF453A', accent:'#FFFFFF', border:'#2A2A2A', tagBg:'#2B2B2D', histBg:'#000000', histItem:'#000000',
  };
  const light = {
    bg:'#FFFFFF', inputBg:'#F5F5F5', btnNum:'#EBEBEB', btnFn:'#EBEBEB', btnOp:'#EBEBEB',
    btnEq:'#4A5240', btnSci:'#EBEBEB', text:'#1C1C1E', textSecondary:'#8A8A8E', exprText:'#1C1C1E',
    cRed:'#FF3B30', accent:'#1C1C1E', border:'#D8D8D8', tagBg:'#EBEBEB', histBg:'#FFFFFF', histItem:'#FFFFFF',
  };
  const c = theme === 'dark' ? dark : light;
  const selCls = theme === 'dark' ? 'calc-sel-d' : 'calc-sel-l';

  // ── Formatted expression (with commas) + cursor mapping ──
  const exprLocale = numberSystem === 'indian' ? 'en-IN' : 'en-US';
  const { formatted: formattedExpression, rawToFmt, fmtToRaw } = useMemo(
    () => buildFormattedExpression(expression, exprLocale),
    [expression, exprLocale],
  );
  // ── Visual expression (formatted + paren spacing) ──
  const { visual: visualExpression, fmtToVis, visToFmt } = useMemo(
    () => buildVisualExpression(formattedExpression),
    [formattedExpression],
  );
  const cursorVisPos = (expression !== '' && cursorPos !== null)
    ? fmtToVis(rawToFmt(cursorPos))
    : null;

  // ── Display click → raw cursor position ──
  const handleDisplayClick = useCallback((e) => {
    if (!expression || !inputRef.current) return;
    let visIdx = visualExpression.length;
    try {
      let range;
      if (document.caretRangeFromPoint) {
        range = document.caretRangeFromPoint(e.clientX, e.clientY);
      } else if (document.caretPositionFromPoint) {
        const pos = document.caretPositionFromPoint(e.clientX, e.clientY);
        if (pos) { range = document.createRange(); range.setStart(pos.offsetNode, pos.offset); }
      }
      if (range && inputRef.current.contains(range.startContainer)) {
        const walker = document.createTreeWalker(inputRef.current, NodeFilter.SHOW_TEXT);
        let offset = 0, node, found = false;
        while ((node = walker.nextNode())) {
          if (node.parentElement?.dataset?.cursor === '1') continue; // skip the blinking | cursor char
          if (node === range.startContainer) { visIdx = offset + range.startOffset; found = true; break; }
          offset += node.textContent.length;
        }
        // If tap landed on the | cursor itself, keep cursor at its current position
        if (!found && cursorVisPos !== null) visIdx = cursorVisPos;
      }
    } catch (err) { console.warn('[handleDisplayClick]', err); }
    const raw = fmtToRaw(visToFmt(visIdx));
    // Snap: if cursor lands on a space inside a ' op ' token, nudge to a number boundary.
    // Only snap LEFT if cursor has entered the op zone past l AND is strictly closer to l.
    // When p===l (at the left edge, right after a digit), always snap right — otherwise
    // the insert would silently append to the left operand instead of going before the right one.
    const snap = (p) => {
      const e = expression;
      if (!e || p >= e.length || e[p] !== ' ') return p; // only fire on spaces
      let l = p, r = p;
      while (l > 0 && (e[l-1] === ' ' || /[+\-×÷^]/.test(e[l-1]))) l--;
      while (r < e.length && (e[r] === ' ' || /[+\-×÷^]/.test(e[r]))) r++;
      return (p > l && p - l < r - p) ? l : r;
    };
    setCursorPos(snap(raw));
  }, [expression, visualExpression, fmtToRaw, visToFmt, cursorVisPos]);

  // ── Number formatting ──
  const formatNumber = useCallback((num) => {
    if (num === null || num === undefined || num === '' || num === 'Error') return num;
    const raw = parseFloat(num);
    if (isNaN(raw) || !isFinite(raw)) return String(num);
    // Always operate on a numeric value — never re-stringify the raw input arg
    const number = roundingEnabled ? Math.round(raw) : raw;
    const sign = number < 0 ? '-' : '';
    const absVal = Math.abs(number);
    let numStr = String(absVal);
    // Expand scientific notation before formatting
    if (numStr.includes('e') || numStr.includes('E')) {
      const expanded = absVal.toPrecision(15).replace(/\.?0+$/, '');
      if (expanded.includes('e')) return sign + expanded; // too large to expand
      numStr = expanded;
    }
    const dotIdx = numStr.indexOf('.');
    const intStr = dotIdx === -1 ? numStr : numStr.slice(0, dotIdx);
    const decStr = dotIdx === -1 ? '' : numStr.slice(dotIdx + 1);
    const intNum = parseInt(intStr, 10);
    const fi = numberSystem === 'indian'
      ? intNum.toLocaleString('en-IN')
      : intNum.toLocaleString('en-US');
    return sign + fi + (decStr ? '.' + decStr : '');
  }, [roundingEnabled, numberSystem]);

  const getPos    = useCallback(() => cursorPos ?? expression.length, [cursorPos, expression]);
  const insertAt  = useCallback((str, pos, text) => ({ expr: str.slice(0,pos)+text+str.slice(pos), newPos: pos+text.length }), []);

  // ── Calculator handlers ──
  const handleNumberPress = useCallback((num) => {
    // If in Error state, any digit starts fresh
    if (result === 'Error') { setExpression(num); setResult('0'); setCursorPos(num.length); return; }
    // 200-char expression limit
    if (expression.length >= 200) { showToast("Can't enter more than 200 characters."); return; }
    if (result !== '0' && expression === '') { setExpression(num); setResult('0'); setCursorPos(num.length); }
    else {
      const pos = getPos();
      // 15-digit limit: count digits in current number token at cursor
      if (/\d/.test(num)) {
        const before = expression.slice(0, pos);
        const after  = expression.slice(pos);
        // Find the current number token surrounding the cursor
        const tokenMatch = (before.match(/\d+\.?\d*$/) || [''])[0] + (after.match(/^\d*\.?\d*/) || [''])[0];
        const digitCount = (tokenMatch.replace('.','').match(/\d/g)||[]).length;
        if (digitCount >= 15) { showToast("Can't enter more than 15 digits."); return; }
      }
      // Prevent inserting a leading zero before another digit (e.g. '066' → formats invisibly as '66')
      if (num === '0') {
        const nextCh = expression[pos];
        const prevCh = pos > 0 ? expression[pos - 1] : '';
        if (/\d/.test(nextCh) && !/[\d.]/.test(prevCh)) return;
      }
      // If the character immediately before cursor is a standalone '0'
      // (preceded by start/operator/open-paren and followed by end/operator/close-paren),
      // replace it instead of inserting beside it — prevents '05', '0+' etc.
      const before = expression.slice(0, pos);
      const after  = expression.slice(pos);
      const soloZero = before.endsWith('0') &&
        /(?:^|[\s(+\-×÷^])0$/.test(before) &&
        /^(?:$|[\s)+\-×÷^])/.test(after);
      if (soloZero) {
        const expr = before.slice(0, -1) + num + after;
        setExpression(expr); setCursorPos(pos); // cursor stays at same position (replaced in-place)
      } else {
        const {expr, newPos} = insertAt(expression, pos, num);
        setExpression(expr); setCursorPos(newPos);
      }
    }
  }, [result, expression, cursorPos]);

  const handleOperator = useCallback((op) => {
    const token = op; // No spaces
    if (expression==='' && result!=='0') { const e=result+token; setExpression(e); setResult('0'); setCursorPos(e.length); }
    else if (expression==='' && result==='0') { return; }
    else {
      const pos=getPos(), atEnd=pos>=expression.length;
      const before = expression.slice(0, pos);
      const after  = expression.slice(pos);
      const opTokenMatch = before.match(/[+\-×÷^]$/); 
      if (opTokenMatch) {
        const t = before.slice(0, -1) + token;
        setExpression(t + after); setCursorPos(t.length);
      } else if (atEnd) {
        const lc=expression.trim().slice(-1);
        if (['+','-','×','÷','^'].includes(lc)) { const t=expression.trimEnd().slice(0,-1).trimEnd()+token; setExpression(t); setCursorPos(t.length); }
        else { const {expr,newPos}=insertAt(expression,pos,token); setExpression(expr); setCursorPos(newPos); }
      } else { const {expr,newPos}=insertAt(expression,pos,token); setExpression(expr); setCursorPos(newPos); }
    }
  }, [expression, result, cursorPos]);

  const showToast = useCallback((msg) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    const id = Date.now();
    setToast({ msg, id });
    toastTimerRef.current = setTimeout(() => setToast(null), 2000);
  }, []);

  // ── Auto-Kick for Expired Passes ──
  // Checks every second. If time is up, it kicks them out to the calculator.
  useEffect(() => {
    if (isFullyPro || isCapacitor()) return;

    const interval = setInterval(() => {
      const now = Date.now();
      if (mode === 'converter' && activePasses['converter'] && now > activePasses['converter']) {
        setMode('calculator'); showToast('Converter pass expired.');
      } else if (mode === 'financial' && activePasses['financial'] && now > activePasses['financial']) {
        setMode('calculator'); showToast('Financial Tools pass expired.');
      } else if (showHistory && activePasses['history'] && now > activePasses['history']) {
        setShowHistory(false); showToast('History pass expired.');
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [mode, showHistory, activePasses, isFullyPro, showToast]);
  
  const handleEquals = useCallback(() => {
    setCursorPos(null);
    if (expression === '') {
      if (lastOperation && result !== '0') {
        const rawResult = result.replace(/,/g, '');
        const ne = rawResult + lastOperation.op + lastOperation.value;
        const calc = evaluateExpression(ne);
        setResult(calc);
        setHistory(h => [`${formatNumber(result)}${lastOperation.op}${formatNumber(lastOperation.value)} = ${formatNumber(calc)}`,...h.slice(0,49)]);
      }
    } else {
      if (/[+\-×÷^]\s*$/.test(expression.trim())) {
        showToast('Invalid format used.');
        return;
      }
      const calc = evaluateExpression(expression);
      setHistory(h => [`${formattedExpression} = ${formatNumber(calc)}`,...h.slice(0,49)]);
      setResult(calc);
      const match = expression.match(/([+\-×÷^])([^+\-×÷^]+)$/);
      if (match) setLastOperation({op: match[1], value: match[2]});
      setExpression('');
    }
  }, [expression, formattedExpression, result, lastOperation, formatNumber, showToast]);

  const handleClear = useCallback(() => { setExpression(''); setResult('0'); setLastOperation(null); setCursorPos(null); }, []);

  const handleBackspace = useCallback(() => {
    if (expression === '') return;
    const pos = getPos();
    if (pos === 0) return;

    const before = expression.slice(0, pos);
    const after = expression.slice(pos);
    
    const newExpr = before === '-' ? after : before.slice(0, -1) + after;
    const newPos = before === '-' ? 0 : pos - 1;

    setExpression(newExpr);
    setCursorPos(newPos); 
  }, [expression, getPos]);

  const handleDecimal = useCallback(() => {
    const pos=getPos(), before=expression.slice(0,pos);
    const parts=before.split(/[+\-×÷%^()\s]+/), last=parts[parts.length-1];
    if (!last.includes('.')) {
      // Auto-prepend 0 if decimal would be the first char of an operand (e.g. "5 + ." → "5 + 0.")
      const needsZero = last === '' || before === '';
      const insert = needsZero ? '0.' : '.';
      const {expr,newPos}=insertAt(expression,pos,insert); setExpression(expr); setCursorPos(newPos);
    }
  }, [expression, cursorPos]);

  const handleParenthesis = useCallback(() => {
    const pos=getPos(), before=expression.slice(0,pos);
    const open=(before.match(/\(/g)||[]).length, close=(before.match(/\)/g)||[]).length;
    const last=before.trimEnd().slice(-1);
    let ch;
    if (open>close&&before!==''&&!/[+\-×÷(]$/.test(before.trimEnd())) ch=')';
    else if (before===''||/[+\-×÷(]/.test(last)) ch='(';
    else ch='×(';
    const {expr,newPos}=insertAt(expression,pos,ch); setExpression(expr); setCursorPos(newPos);
  }, [expression, cursorPos]);

  const handleToggleSign = useCallback(() => {
    if (expression==='') { setResult(prev => prev.startsWith('-')?prev.slice(1):'-'+prev); return; }
    const pos=getPos(), before=expression.slice(0,pos);
    const m=before.match(/^(.*?)(-?\d+\.?\d*)(\)*)$/);
    if (m) {
      const [,prefix,num,trailing]=m;
      if (num.startsWith('-')) {
        // Un-negate: strip the '(' we inserted before '-', and matching ')' if present
        const hasOpenParen = prefix.endsWith('(');
        let r, removedChars;
        if (hasOpenParen) {
          const newTrailing = trailing.startsWith(')') ? trailing.slice(1) : trailing;
          r = prefix.slice(0, -1) + num.slice(1) + newTrailing;
          removedChars = 2; // removed '(' and '-'
        } else {
          r = prefix + num.slice(1) + trailing;
          removedChars = 1; // removed '-' only
        }
        // Keep cursor at same logical position, adjusted for removed chars
        setExpression(r+expression.slice(pos)); setCursorPos(Math.max(0, pos - removedChars));
      } else if (prefix===''&&trailing===''&&expression.slice(pos)==='') {
        setExpression('-'+num); setCursorPos(('-'+num).length);
      } else {
        const r=prefix+'(-'+num+')'+trailing;
        setExpression(r+expression.slice(pos)); setCursorPos(r.length);
      }
      return;
    }
    setExpression('-('+expression); setCursorPos(pos+2);
  }, [expression, cursorPos]);

  const handlePercent = useCallback(() => {
    if (expression==='') {
      const val=parseFloat(result); if (isNaN(val)) return;
      const pct=val/100; setResult(smartStr(pct));
      setHistory(h=>[formatNumber(result)+'% = '+formatNumber(smartStr(pct)),...h.slice(0,49)]);
      return;
    }
    const pos = getPos();
    const atEnd = pos >= expression.length;
    if (atEnd) {
      const m=expression.trim().match(/^(.+?)\s*([+\-×÷])\s*(-?\(?[\d.]+\)?)\s*$/);
      if (m) {
        const [,lp,op,rs]=m;
        const rv=parseFloat(rs.replace(/[()]/g,'')), lv=parseFloat(evaluateExpression(lp));
        const pv=(op==='+'||op==='-')?lv*rv/100:rv/100;
        const calc=evaluateExpression(lp+op+pv);
        setHistory(h=>[expression+'% = '+formatNumber(String(calc)),...h.slice(0,49)]);
        setResult(String(calc)); setExpression(''); setCursorPos(null); return;
      }
    }
    const {expr,newPos}=insertAt(expression,pos,'%'); setExpression(expr); setCursorPos(newPos);
  }, [expression, result, formatNumber, cursorPos]);

  // Fix 4: memoized so handleSci's dep array doesn't trigger re-creation on every render
  const toRad   = useCallback((x) => angleMode==='deg' ? x*Math.PI/180 : x, [angleMode]);
  const fromRad = useCallback((x) => angleMode==='deg' ? x*180/Math.PI : x, [angleMode]);

  const handleSci = useCallback((op) => {
    const isPlain=expression!==''&&/^-?\d+\.?\d*$/.test(expression.trim());
    const x=parseFloat(isPlain?expression:result), disp=isPlain?expression:result;
    if (op==='π'){setResult(smartStr(Math.PI));setExpression('');setCursorPos(null);return;}
    if (op==='e'){setResult(smartStr(Math.E));setExpression('');setCursorPos(null);return;}
    if (op==='xʸ'){
      const isPlainExpr = /^-?\d+\.?\d*$/.test((expression||'').trim());
      const base = expression
        ? (isPlainExpr ? expression : evaluateExpression(expression))
        : result;
      if (!base || base === 'Error' || !isFinite(parseFloat(base))) {
        showToast('Invalid base for xʸ'); return;
      }
      const t = base + ' ^ ';
      setExpression(t); setCursorPos(t.length); return;
    }
    if (isNaN(x)){setResult('Error');setExpression('');setCursorPos(null);return;}
    let y;
    switch(op){
      case 'sin':y=Math.sin(toRad(x));break; case 'cos':y=Math.cos(toRad(x));break; case 'tan':y=Math.tan(toRad(x));break;
      case 'sin⁻¹':y=fromRad(Math.asin(x));break; case 'cos⁻¹':y=fromRad(Math.acos(x));break; case 'tan⁻¹':y=fromRad(Math.atan(x));break;
      case 'sinh':y=Math.sinh(x);break; case 'cosh':y=Math.cosh(x);break; case 'tanh':y=Math.tanh(x);break;
      case 'sinh⁻¹':y=Math.asinh(x);break; case 'cosh⁻¹':y=Math.acosh(x);break; case 'tanh⁻¹':y=Math.atanh(x);break;
      case 'ln':y=Math.log(x);break; case 'log':y=Math.log10(x);break; case '1/x':y=1/x;break;
      case 'x²':y=x*x;break; case 'x³':y=x*x*x;break; case '2ˣ':y=Math.pow(2,x);break;
      case 'eˣ':y=Math.exp(x);break; case '√':y=Math.sqrt(x);break; case '³√':y=Math.cbrt(x);break;
      case '|x|':y=Math.abs(x);break; case 'x!':y=factorial(x);break; case '10ˣ':y=Math.pow(10,x);break; default:return;
    }
    const out=smartStr(y);
    if (out==='Error'){setResult('Error');setExpression('');setCursorPos(null);return;}
    setHistory(h=>[op+'('+formatNumber(disp)+') = '+formatNumber(out),...h.slice(0,49)]);
    setResult(out); setExpression(''); setCursorPos(null);
  }, [expression, result, toRad, fromRad, angleMode, formatNumber, showToast]);

  const loadFromHistory = useCallback((item) => {
    const eq = item.lastIndexOf(' = ');
    if (eq>=0) { setResult(item.slice(eq+3).replace(/,/g,'')); setExpression(''); }
    setShowHistory(false);
  }, []);

  // ── Keyboard support ──
  useEffect(() => {
    if (mode !== 'calculator') return;
    const handler = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag==='INPUT'||tag==='SELECT'||tag==='TEXTAREA') return;
      const k = e.key;
      if (k>='0'&&k<='9'){e.preventDefault();handleNumberPress(k);}
      else if(k==='+'){e.preventDefault();handleOperator('+');}
      else if(k==='-'){e.preventDefault();handleOperator('-');}
      else if(k==='*'){e.preventDefault();handleOperator('×');}
      else if(k==='/'){e.preventDefault();handleOperator('÷');}
      else if(k==='Enter'||k==='='){e.preventDefault();handleEquals();}
      else if(k==='Backspace'){e.preventDefault();handleBackspace();}
      else if(k==='Escape'){e.preventDefault();handleClear();}
      else if(k==='.'){e.preventDefault();handleDecimal();}
      else if(k==='%'){e.preventDefault();handlePercent();}
      else if(k==='('||k===')'){e.preventDefault();handleParenthesis();}
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mode, handleNumberPress, handleOperator, handleEquals, handleBackspace, handleClear, handleDecimal, handlePercent, handleParenthesis]);

  const handleConverterTypeChange = useCallback((type) => {
    setConverterType(type);
    if (type === 'currency') {
      setFromUnit('USD'); setToUnit('INR');
    } else {
      const keys=Object.keys(UNITS[type]);
      setFromUnit(keys[0]||''); setToUnit(keys[1]||keys[0]||'');
    }
    setFromValue(''); setToValue(''); setCvActiveField('from');
  }, [converterType]);
  const cvClear    = useCallback(() => {
    setFromValue(''); setToValue('');
    requestAnimationFrame(() => {
      const ref = cvActiveField==='from' ? fromInputRef : toInputRef;
      ref.current?.focus();
    });
  }, [cvActiveField]);
  // Dispatches to currency or unit converter depending on active tab
  const cvConvert = useCallback((value, from, to) => {
    if (converterType === 'currency') {
      if (!fxRates || !fxRates[from] || !fxRates[to]) return '';
      const num = parseFloat(value);
      if (isNaN(num)) return '';
      return String(parseFloat((num / fxRates[from] * fxRates[to]).toPrecision(8)));
    }
    return convertUnit(value, from, to, converterType);
  }, [converterType, fxRates]);

  const cvToggleSign=useCallback(() => {
    const cur = cvActiveField==='from'?fromValue:toValue;
    if(!cur)return;
    const nv = cur.startsWith('-')?cur.slice(1):'-'+cur;
    if(cvActiveField==='from'){setFromValue(nv);setToValue(nv?cvConvert(nv,fromUnit,toUnit):'');}
    else{setToValue(nv);setFromValue(nv?cvConvert(nv,toUnit,fromUnit):'');}
    requestAnimationFrame(() => {
      const ref = cvActiveField==='from' ? fromInputRef : toInputRef;
      ref.current?.focus();
    });
  }, [cvActiveField, fromValue, toValue, fromUnit, toUnit, converterType, cvConvert]);
  // Format a raw converter value for display (commas, no scientific notation issues)
  // Fix 2: wrapped in useCallback so cvPress/cvBackspace/cvHandleChange capture a stable,
  // up-to-date reference and won't use a stale formatNumber after locale/rounding changes.
  const cvFormat = useCallback((raw) => {
    if (!raw || raw === '-') return raw;
    const n = parseFloat(raw);
    if (isNaN(n)) return raw;
    return formatNumber(raw);
  }, [formatNumber]);

  // ── Cursor helpers for formatted converter inputs ──
  // How many non-comma chars appear before fmtPos in formatted string
  const cvFmtToRaw = (fmt, fmtPos) => fmt.slice(0, fmtPos).replace(/,/g, '').length;
  // Which formatted position corresponds to rawPos non-comma chars
  const cvRawToFmt = (raw, rawPos) => {
    const fmt = cvFormat(raw) || '';
    if (rawPos <= 0) return 0;
    let digits = 0;
    for (let i = 0; i < fmt.length; i++) {
      if (fmt[i] !== ',') digits++;
      if (digits === rawPos) return i + 1;
    }
    return fmt.length;
  };
  // Get cursor position in the raw string from the active input ref
  const cvGetRawCursor = (field, rawVal) => {
    const el = (field === 'from' ? fromInputRef : toInputRef).current;
    if (!el) return (rawVal || '').length;
    const fmt = cvFormat(rawVal) || '';
    return cvFmtToRaw(fmt, el.selectionStart ?? fmt.length);
  };

  // Direct-typing handler — strips commas, validates, restores cursor synchronously
  const cvHandleChange = useCallback((field, e) => {
    const el = e.target;
    const fmtBefore = el.value;
    const selEnd = el.selectionEnd ?? fmtBefore.length;
    const rawInput = fmtBefore.replace(/,/g, '');
    if (rawInput !== '' && rawInput !== '-' && !/^-?\d*\.?\d*$/.test(rawInput)) return;
    // Raw cursor = non-comma chars before selEnd in old formatted string
    const rawCursor = cvFmtToRaw(fmtBefore, selEnd);
    // New formatted cursor after state update
    const newFmtCursor = cvRawToFmt(rawInput, rawCursor);
    // Set ref synchronously so useLayoutEffect sees it on the very next render
    cvCursorRef.current = { field, pos: newFmtCursor };
    setCvActiveField(field);
    if (field === 'from') {
      setFromValue(rawInput);
      setToValue(rawInput ? cvConvert(rawInput, fromUnit, toUnit) : '');
    } else {
      setToValue(rawInput);
      setFromValue(rawInput ? cvConvert(rawInput, toUnit, fromUnit) : '');
    }
  }, [fromUnit, toUnit, converterType, cvFormat, cvConvert]);

  // Numpad press — appends digit/dot to the active field (no DOM cursor needed)
  const cvPress = useCallback((num) => {
    const field = cvActiveField;
    const rawVal = field === 'from' ? fromValue : toValue;
    let nv;
    if (num === '.') {
      if (rawVal.includes('.')) return; // already has decimal
      nv = (rawVal || '0') + '.';
    } else {
      // Replace a leading solo zero
      if (rawVal === '0') nv = num;
      else nv = (rawVal || '') + num;
    }
    if (field === 'from') { setFromValue(nv); setToValue(nv ? cvConvert(nv, fromUnit, toUnit) : ''); }
    else                  { setToValue(nv);   setFromValue(nv ? cvConvert(nv, toUnit, fromUnit) : ''); }
  }, [cvActiveField, fromValue, toValue, fromUnit, toUnit, converterType, cvConvert]);

  // Numpad backspace — deletes char before cursor position
  const cvBackspace = useCallback(() => {
    const field = cvActiveField;
    const rawVal = field === 'from' ? fromValue : toValue;
    if (!rawVal) return;
    const nv = rawVal.slice(0, -1); // pop last char
    if (field === 'from') { setFromValue(nv); setToValue(nv ? cvConvert(nv, fromUnit, toUnit) : ''); }
    else                  { setToValue(nv);   setFromValue(nv ? cvConvert(nv, toUnit, fromUnit) : ''); }
  }, [cvActiveField, fromValue, toValue, fromUnit, toUnit, converterType, cvConvert]);

  useEffect(() => {
    if (mode !== 'converter') return;
    const ref = cvActiveField === 'from' ? fromInputRef : toInputRef;
    ref.current?.focus();
  }, [mode, cvActiveField]);

  // ── Financial calcs ──
  // days between two yyyy-mm-dd strings
  const dateDiff = (from, to) => {
    if (!from||!to) return null;
    const a=new Date(from), b=new Date(to);
    if (isNaN(a)||isNaN(b)||b<=a) return null;
    const totalDays=Math.round((b-a)/86400000);
    let yr=b.getFullYear()-a.getFullYear(), mo=b.getMonth()-a.getMonth(), d=b.getDate()-a.getDate();
    if(d<0){mo--;const prev=new Date(b.getFullYear(),b.getMonth(),0);d+=prev.getDate();}
    if(mo<0){yr--;mo+=12;}
    return {totalDays,years:yr,months:mo,days:d,exactYears:totalDays/365.25};
  };
  const calculateEMI = () => {
    if (!principal||!rate) return;
    let n;
    if (emiDateMode==='date') {
      const diff=dateDiff(emiFromDate,emiToDate); if(!diff) return;
      n=diff.totalDays/30.4375;
    } else {
      if(!tenureYears&&!tenureMonths&&!tenureDays) return;
      n=parseFloat(tenureYears||0)*12+parseFloat(tenureMonths||0)+parseFloat(tenureDays||0)/30;
    }
    const P=parseFloat(principal),r=parseFloat(rate)/100/12;
    if(n<=0)return;
    if(r===0){const e=P/n;setEmiResult({emi:sm(e),totalAmount:sm(P),totalInterest:'0',principal:sm(P)});return;}
    const emi=(P*r*Math.pow(1+r,n))/(Math.pow(1+r,n)-1);
    const total=emi*n; setEmiResult({emi:sm(emi),totalAmount:sm(total),totalInterest:sm(total-P),principal:sm(P)});
  };
  const calculateSIP = () => {
    if (!sipAmount||!sipRate) return;
    let yrs;
    if (sipDateMode==='date') {
      const diff=dateDiff(sipFromDate,sipToDate); if(!diff) return;
      yrs=diff.exactYears;
    } else {
      if (!sipYears) return; yrs=parseFloat(sipYears);
    }
    const P=parseFloat(sipAmount),r=parseFloat(sipRate)/100/12,n=yrs*12;
    const fv=r===0?P*n:P*(((Math.pow(1+r,n)-1)/r)*(1+r));
    setSipResult({futureValue:sm(fv),invested:sm(P*n),returns:sm(fv-P*n)});
  };
  const calculateRD = () => {
    if (!rdAmount || !rdRate) return;
    
    let exactMonths;
    let totalDeposits;
    
    if (rdDateMode === 'date') {
      const diff = dateDiff(rdFromDate, rdToDate); if (!diff) return;
      exactMonths = diff.exactYears * 12; 
      totalDeposits = Math.floor(exactMonths); 
    } else {
      if (!rdYears && !rdMonthsPart && !rdDaysPart) return;
      // Convert days to fractional months (using 30.4375 avg days per month)
      exactMonths = (parseFloat(rdYears||0) * 12) + parseFloat(rdMonthsPart||0) + (parseFloat(rdDaysPart||0) / 30.4375);
      totalDeposits = Math.floor(exactMonths);
    }
    
    if (exactMonths <= 0 || totalDeposits <= 0) return;

    const P = parseFloat(rdAmount);
    const annualRate = parseFloat(rdRate) / 100;
    
    const freqMap = { monthly: 12, quarterly: 4, 'half-yearly': 2, yearly: 1 };
    const compFreq = freqMap[rdFreq] || 4; 
    const r = annualRate / compFreq;
    
    let maturity = 0;
    
    for (let i = 1; i <= totalDeposits; i++) {
      const monthsRemaining = exactMonths - i + 1;
      const periodsRemaining = monthsRemaining / (12 / compFreq);
      maturity += P * Math.pow(1 + r, periodsRemaining);
    }
    
    const invested = P * totalDeposits;
    const interest = maturity - invested;
    
    const displayMonths = Number.isInteger(exactMonths) ? exactMonths : parseFloat(exactMonths.toFixed(2));
    
    setRdResult({ maturity: sm(maturity), invested: sm(invested), interest: sm(interest), months: displayMonths });
  };
  const calculateInterest = () => {
    if (!interestPrincipal||!interestRate) return;
    let t;
    if (intDateMode==='date') {
      const diff=dateDiff(intFromDate,intToDate); if(!diff) return;
      t=diff.totalDays/365.25;
    } else {
      if(!interestYears&&!interestMonths&&!interestDays) return;
      t=parseFloat(interestYears||0)+parseFloat(interestMonths||0)/12+parseFloat(interestDays||0)/365;
    }
    const P=parseFloat(interestPrincipal),r=parseFloat(interestRate)/100;
    if(interestType==='simple'){const i=P*r*t;setInterestResult({interest:sm(i),totalAmount:sm(P+i),principal:sm(P)});}
    else{const n={annually:1,'semi-annually':2,quarterly:4,monthly:12,daily:365}[compoundFrequency]||1;const total=P*Math.pow(1+r/n,n*t);setInterestResult({interest:sm(total-P),totalAmount:sm(total),principal:sm(P)});}
  };
  const calculateDiscount = () => {
    if(discountMode==='find'){if(!originalPrice||!discountFinalPrice)return;const orig=parseFloat(originalPrice),fin=parseFloat(discountFinalPrice);if(orig<=0||fin>orig)return;const dA=orig-fin;setDiscountResult({mode:'find',originalPrice:sm(orig),discountAmount:sm(dA),finalPrice:sm(fin),savings:sm((dA/orig)*100)+'%'});return;}
    if(discountMode==='forward'){if(!originalPrice||!discountPercent)return;const orig=parseFloat(originalPrice),disc=parseFloat(discountPercent);const dA=(orig*disc)/100;setDiscountResult({mode:'forward',originalPrice:sm(orig),discountAmount:sm(dA),finalPrice:sm(orig-dA),savings:disc+'%'});}
    else{if(!discountFinalPrice||!discountPercent)return;const fin=parseFloat(discountFinalPrice),disc=parseFloat(discountPercent);if(disc>=100)return;const orig=fin/(1-disc/100);setDiscountResult({mode:'reverse',originalPrice:sm(orig),discountAmount:sm(orig-fin),finalPrice:sm(fin),savings:disc+'%'});}
  };
  const calculateGST = () => {
    if(!gstAmount||!gstRate)return;
    const amt=parseFloat(gstAmount),r=parseFloat(gstRate);
    if(gstType==='add'){const gst=amt*r/100,total=amt+gst;setGstResult({type:'add',baseAmount:sm(amt),gstAmount:sm(gst),totalAmount:sm(total),rate:r+'%',cgst:sm(gst/2),sgst:sm(gst/2)});}
    else{const base=amt/(1+r/100),gst=amt-base;setGstResult({type:'remove',baseAmount:sm(base),gstAmount:sm(gst),totalAmount:sm(amt),rate:r+'%',cgst:sm(gst/2),sgst:sm(gst/2)});}
  };
  const calculateMargin = () => {
    if(!costPrice||!sellingPrice)return;
    const cost=parseFloat(costPrice),selling=parseFloat(sellingPrice);
    if(cost===0){setMarginResult({profit:sm(selling),profitPercent:'N/A',marginPercent:'100',status:'Profit'});return;}
    const profit=selling-cost;
    setMarginResult({profit:sm(Math.abs(profit)),profitPercent:sm(Math.abs((profit/cost)*100)),marginPercent:sm(Math.abs((profit/selling)*100)),status:profit>=0?'Profit':'Loss'});
  };
  const calculateROI = () => {
    if(!initialInvestment||!finalValue)return;
    const initial=parseFloat(initialInvestment),fv=parseFloat(finalValue),gain=fv-initial;
    if(initial===0){setRoiResult({totalReturn:sm(fv),roiPercent:'N/A',annualizedReturn:null});return;}
    let yrs=null;
    if (roiDateMode==='date'){const diff=dateDiff(roiFromDate,roiToDate);if(diff)yrs=diff.exactYears;}
    else if(roiYears)yrs=parseFloat(roiYears);
    setRoiResult({totalReturn:sm(gain),roiPercent:sm((gain/initial)*100),annualizedReturn:yrs?sm(((Math.pow(fv/initial,1/yrs))-1)*100):null});
  };
  const calculateGratuity = () => {
    if(!lastSalary||!yearsOfService)return;
    setGratuityResult({gratuity:sm((parseFloat(lastSalary)*15*parseFloat(yearsOfService))/26),yearsOfService:parseFloat(yearsOfService),lastSalary:sm(parseFloat(lastSalary))});
  };
  const calculateFuelEconomy = () => {
    if(!distance||!fuelUsed)return;
    let dist=parseFloat(distance),fuel=parseFloat(fuelUsed);
    if(distanceUnit==='mile')dist*=1.60934; if(fuelUnit==='gallon')fuel*=3.78541;
    const kpl=dist/fuel,l100=(fuel/dist)*100,mpg=kpl*2.35215;
    let tc=null,cpk=null;
    if(fuelPrice){const pr=parseFloat(fuelPrice);tc=fuel*pr;cpk=tc/dist;}
    setFuelEconomyResult({kmPerLiter:sm(kpl),litersPer100km:sm(l100),mpg:sm(mpg),totalCost:tc?sm(tc):null,costPerKm:cpk?sm(cpk):null});
  };

  // ── UI helpers ──
  const MD  = (e) => e.preventDefault();
  // vib: wraps a click handler to fire haptic first
  // Use onPointerDown for instant response on number buttons
  const vib = useCallback((fn, ms = 10) => (e) => { vibrate(ms); fn(e); }, [vibrate]);
  // vibPress: fires immediately on pointer down for snappier feel
  const vibPress = useCallback((fn) => (e) => { e.preventDefault(); vibrate(10); fn(); }, [vibrate]);
  const optLabel = (u) => { const s=UNIT_SYMBOLS[u]; return s?`${u} (${s})`:u; };
  const accentActive = theme==='dark' ? '#000' : '#fff';

  const livePreview = useMemo(() => {
    if (!expression) return null;

    // 1. Strip away any trailing operators or open parentheses 
    // Example: "1+2+" becomes "1+2"
    const cleanedExpr = expression.replace(/[+\-×÷^(\s]+$/, '').trim();

    // 2. If the cleaned expression is just a single number (or empty), hide preview
    // Example: "1" or "1+" both evaluate to just "1". No math to show!
    if (!cleanedExpr || /^-?\d*\.?\d*$/.test(cleanedExpr)) return null;

    // 3. Otherwise, evaluate the cleaned expression!
    // Example: "1+2+" cleanly evaluates the "1+2" part and shows "3"
    const v = evaluateExpression(cleanedExpr);
    if (!v || v === 'Error' || !isFinite(parseFloat(String(v)))) return null;
    
    return String(v);
  }, [expression]);

  const chip = (active) => ({
    padding:'6px 18px', borderRadius:'50px', border:'none', cursor:'pointer', whiteSpace:'nowrap',
    fontFamily:FONT, fontSize:'0.875rem', fontWeight: active ? 600 : 400, flexShrink:0,
    background: active ? (theme==='dark'?'#3A3A3C':'#D8D8D8') : 'transparent',
    color: active ? c.text : c.textSecondary,
    transition:'background 0.15s',
  });
  const finChip = (active) => ({
    padding:'7px 14px', borderRadius:'50px', border:'none', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0,
    fontFamily:FONT, fontSize:'0.82rem', fontWeight: active ? 600 : 400, display:'flex', alignItems:'center', gap:'4px',
    background: active ? (theme==='dark'?'#3A3A3C':'#D8D8D8') : 'transparent',
    color: active ? c.text : c.textSecondary,
  });
  const iconBtn = (active) => ({
    background:'none', border:'none', cursor:'pointer', padding:'10px', borderRadius:'50%',
    color: active ? c.text : c.textSecondary, display:'flex', alignItems:'center', justifyContent:'center',
  });

  const calcActionBtn = (onClick, label) => (
    <button onClick={onClick} style={{ width:'100%', padding:'14px', borderRadius:'16px', border:'none',
      background:c.btnEq, color:'#FFFFFF', fontFamily:FONT, fontWeight:600, fontSize:'1rem', cursor:'pointer', marginTop:'8px' }}>
      {label}
    </button>
  );

  const DateRangeField = ({fromDate,setFromDate,toDate,setToDate}) => {
    const diff=dateDiff(fromDate,toDate);
    const parts=diff?[diff.years?`${diff.years}y`:'',diff.months?`${diff.months}m`:'',diff.days?`${diff.days}d`:''].filter(Boolean):[];
    const breakdown=parts.join(' ');
    return (
      <div style={{marginBottom:'14px'}}>
        <label style={{display:'block',fontSize:'0.75rem',marginBottom:'8px',fontWeight:500,color:c.textSecondary,fontFamily:FONT}}>Date Range</label>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',marginBottom:diff?'8px':'0'}}>
          {[['FROM',fromDate,setFromDate],['TO',toDate,setToDate]].map(([lbl,val,setter])=>(
            <div key={lbl}>
              <label style={{display:'block',fontSize:'0.65rem',marginBottom:'4px',color:c.textSecondary,fontFamily:FONT,fontWeight:700,letterSpacing:'0.05em'}}>{lbl}</label>
              <input type="date" value={val} onChange={e=>setter(e.target.value)}
                style={{width:'100%',padding:'10px',borderRadius:'12px',outline:'none',fontWeight:500,
                  backgroundColor:c.inputBg,color:val?c.text:c.textSecondary,
                  border:`1px solid ${val&&lbl==='FROM'&&toDate&&dateDiff(val,toDate)===null?'#FF453A':c.border}`,
                  fontFamily:FONT,fontSize:'0.88rem',boxSizing:'border-box',colorScheme:theme==='dark'?'dark':'light'}}/>
            </div>
          ))}
        </div>
        {diff&&(
          <div style={{background:c.inputBg,borderRadius:'10px',padding:'8px 14px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontSize:'0.78rem',color:c.textSecondary,fontFamily:FONT}}>Duration</span>
            <div style={{textAlign:'right'}}>
              <span style={{fontSize:'1rem',fontWeight:700,color:c.text,fontFamily:FONT}}>{diff.totalDays} days</span>
              {breakdown&&<span style={{fontSize:'0.72rem',color:c.textSecondary,fontFamily:FONT,marginLeft:'8px'}}>({breakdown})</span>}
            </div>
          </div>
        )}
      </div>
    );
  };
  const dateModeToggle = (mode,setMode) => (
    <div style={{display:'flex',gap:'6px',marginBottom:'14px'}}>
      {[['manual','Manual'],['date','Date Range']].map(([id,lbl])=>(
        <button key={id} onClick={()=>setMode(id)} style={finChip(mode===id)}>{lbl}</button>
      ))}
    </div>
  );
  const tenureRow = (y,setY,mo,setMo,d,setD) => (
    <div style={{marginBottom:'14px'}}>
      <label style={{display:'block',fontSize:'0.75rem',marginBottom:'8px',fontWeight:500,color:c.textSecondary,fontFamily:FONT}}>Tenure</label>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'8px'}}>
        {[['Years',y,setY,'yr'],['Months',mo,setMo,'mo'],['Days',d,setD,'d']].map(([lbl,val,setter,unit])=>(
          <div key={lbl} style={{position:'relative'}}>
            <input type="text" inputMode="numeric" pattern="[0-9]*" value={val}
              onChange={e=>{if(e.target.value===''||/^\d*$/.test(e.target.value))setter(e.target.value);}}
              placeholder={lbl}
              style={{width:'100%',padding:'10px',paddingRight:'26px',borderRadius:'12px',outline:'none',fontWeight:500,
                backgroundColor:c.inputBg,color:c.text,border:`1px solid ${c.border}`,caretColor:c.accent,
                fontFamily:FONT,fontSize:'0.9rem',boxSizing:'border-box'}}/>
            <span style={{position:'absolute',right:'7px',top:'50%',transform:'translateY(-50%)',color:c.textSecondary,
              fontSize:'0.65rem',fontWeight:700,pointerEvents:'none',fontFamily:FONT}}>{unit}</span>
          </div>
        ))}
      </div>
    </div>
  );

  // ── Settings panel ──
  const renderSettings = () => {
    const settingBtn = (onClick, icon, label, active) => (
      <button onMouseDown={MD} onClick={onClick} style={{
        flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
        gap:'6px', padding:'12px 4px 10px', borderRadius:'16px', border:`1.5px solid ${active ? c.accent : c.border}`,
        cursor:'pointer', background: active ? (theme==='dark'?'rgba(138,180,248,0.12)':'rgba(0,122,255,0.08)') : c.btnFn,
        transition:'all 0.22s cubic-bezier(0.4,0,0.2,1)', fontFamily:FONT,
      }}>
        <span style={{fontSize:'1.45rem',lineHeight:1}}>{icon}</span>
        <span style={{fontSize:'0.7rem',fontWeight:600,color: active ? c.accent : c.textSecondary,
          letterSpacing:'0.02em',textTransform:'uppercase'}}>{label}</span>
      </button>
    );
    return (
      <div style={{backgroundColor:c.inputBg,borderRadius:'16px',margin:'0',padding:'10px',border:`1px solid ${c.border}`,flexShrink:0,boxShadow:'0 4px 20px rgba(0,0,0,0.35)'}}>
        <div style={{display:'flex',gap:'8px'}}>
          {settingBtn(
            () => setTheme(t => t==='dark' ? 'light' : 'dark'),
            theme==='dark' ? '🌙' : '☀️',
            theme==='dark' ? 'Dark' : 'Light',
            true,
          )}
          {settingBtn(
            () => setNumberSystem(s => s==='international' ? 'indian' : 'international'),
            numberSystem==='international' ? '1K' : '1L',
            numberSystem==='international' ? 'Intl' : 'Indian',
            true,
          )}
          {settingBtn(
            () => setRoundingEnabled(r => !r),
            roundingEnabled ? '⌊x⌋' : 'x.xx',
            roundingEnabled ? 'Integer' : 'Decimal',
            roundingEnabled,
          )}
          {!adsRemoved && isCapacitor() && settingBtn(
            () => setShowRemoveAds(true),
            '🚫',
            'No Ads',
            false,
          )}
        </div>
        {/* Privacy Policy link — required for Google OAuth verification */}
        {/*<div style={{ textAlign: 'center', paddingTop: '8px' }}>
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: '0.7rem',
              color: c.textSecondary,
              textDecoration: 'none',
              fontFamily: FONT,
              opacity: 0.6,
            }}
          >
            Privacy Policy
          </a>
        </div>*/}
      </div>
    );
  };

  // ── Samsung-style Toolbar (icon row + separator) ──
  // This renders between display and keypad on all modes
  const renderSamsungToolbar = (rightSlot) => (
    <div style={{flexShrink:0,backgroundColor:c.bg}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'2px 6px'}}>
        {/* Left icons */}
        <div style={{display:'flex',alignItems:'center'}}>
          {/* History Icon */}
          <button onMouseDown={MD}
            onClick={() => {
              // FIX: If History is already open, ALWAYS let the user close it safely!
              if (showHistory) {
                setShowHistory(false);
              } else {
                // OPEN for Android OR Pro Web users. LOCKED for Free Web users.
                if (isCapacitor() || isFullyPro || hasActivePass('history')) {
                  setMode('calculator'); setShowHistory(true); setShowSettings(false);
                } else {
                  setPremiumTarget('history'); setShowPremiumPaywall(true);
                }
              }
            }}
            style={iconBtn(showHistory && mode === 'calculator')}>
            <div style={{ position: 'relative', display: 'inline-flex' }}>
              {showHistory && mode === 'calculator' ? <Calculator size={22} /> : <History size={22} />}
              {/* Hide the lock if they are on Android, already Pro, or inside History and an active pass */}
              {(!isFullyPro && !showHistory && !isCapacitor() && !hasActivePass('history')) && (
                <Lock size={10} color="#FFD700" style={{ position: 'absolute', bottom: -4, right: -6, background: c.bg, borderRadius: '50%', padding: '2px' }} />
              )}
            </div>
          </button>

          {/* Converter Icon */}
          {mode !== 'converter' && (
            <button onMouseDown={MD}
              onClick={() => {
                // OPEN for Android OR Pro Web users. LOCKED for Free Web users.
                if (isCapacitor() || isFullyPro || hasActivePass('converter')) {
                  setMode('converter'); setShowHistory(false); setShowSettings(false);
                } else {
                  setPremiumTarget('converter'); setShowPremiumPaywall(true);
                }
              }}
              style={iconBtn(false)}>
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <Ruler size={22} />
                {/* Hide the lock if they are on Android or already Pro or an active pass */}
                {(!isFullyPro && !isCapacitor() && !hasActivePass('converter')) && (
                  <Lock size={10} color="#FFD700" style={{ position: 'absolute', bottom: -4, right: -6, background: c.bg, borderRadius: '50%', padding: '2px' }} />
                )}
              </div>
            </button>
          )}

          {/* Scientific: only on calculator mode AND not on tablet (since it's permanently docked) */}
          {mode==='calculator' && !isTablet && (
            <button onMouseDown={MD}
              onClick={()=>{
                setShowHistory(false);
                if (!showSci) {
                  setShowSci(true);
                  requestAnimationFrame(()=>requestAnimationFrame(()=>setSciVisible(true)));
                } else {
                  setSciVisible(false);
                  setTimeout(()=>setShowSci(false), 320);
                }
              }}
              style={iconBtn(showSci)}>
              <FlaskConical size={22}/>
            </button>
          )}

          {/* Financial Icon */}
          {mode !== 'financial' && mode !== 'converter' && (
            <button onMouseDown={MD}
              onClick={() => {
                if (isFullyPro || (isCapacitor() && financialUnlocked) || hasActivePass('financial')) { 
                  setMode('financial'); setShowHistory(false); setShowSettings(false);
                } else if (!isCapacitor()) { 
                  setPremiumTarget('financial'); setShowPremiumPaywall(true);
                } else { 
                  showRewardedAd({
                    onGranted: () => { setMode('financial'); setShowHistory(false); setShowSettings(false); },
                    onDenied: (msg) => { showToast(msg || 'Watch the full ad to unlock Financial Tools'); },
                  });
                }
              }}
              style={iconBtn(false)}>
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <DollarSign size={22} />
                {(!isFullyPro && !(isCapacitor() && financialUnlocked) && !hasActivePass('financial')) && (
                  <Lock size={10} color="#FFD700" style={{ position: 'absolute', bottom: -4, right: -6, background: c.bg, borderRadius: '50%', padding: '2px' }} />
                )}
              </div>
            </button>
          )}

          {/* 📱 PWA INSTALL BUTTON (Beside Financial) */}
          {showInstallBtn && (
            <button onMouseDown={MD}
              onClick={async () => {
                if (deferredPrompt) {
                  deferredPrompt.prompt();
                  const { outcome } = await deferredPrompt.userChoice;
                  if (outcome === 'accepted') {
                    setDeferredPrompt(null);
                    setShowInstallBtn(false);
                  }
                } else if (isIOSBrowser) {
                  showToast("Tap the Share icon ⍗ then select 'Add to Home Screen'");
                } else {
                  showToast("Open your browser menu and select 'Install App' or 'Add to Home Screen'");
                }
              }}
              style={{
                background: theme === 'dark' ? '#2B2B2D' : '#EBEBEB',
                border: 'none', cursor: 'pointer', 
                padding: '8px 16px', borderRadius: '50px',
                color: c.text, display: 'flex', alignItems: 'center', 
                justifyContent: 'center', gap: '8px', marginLeft: '4px',
                fontFamily: FONT, fontSize: '0.95rem', fontWeight: 600
              }}
              title="Install App"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              Install
            </button>
          )}
          {/* END INSTALL BUTTON */}

        </div>
        {/* Right icons */}
        <div style={{display:'flex',alignItems:'center',position:'relative'}}>
          {rightSlot}
          <button onMouseDown={MD} onClick={()=>setShowSettings(s=>!s)} style={iconBtn(showSettings)}>
            <Settings size={20}/>
          </button>
          {/* Settings dropdown — floats below the settings button */}
          {showSettings && (
            <div style={{
              position:'absolute', top:'100%', right:0, zIndex:200,
              marginTop:'4px', minWidth:'220px',
            }}>
              {renderSettings()}
            </div>
          )}
        </div>
      </div>
      {/* Separator line */}
      <div style={{height:'1px',backgroundColor:c.border,margin:'0 2px'}}/>
    </div>
  );



  // ── Scientific panel ──
  const sciRows = {
    primary:[
      [['⇆','swap'],['Deg/Rad','toggle'],['√','sci'],['|x|','sci']],
      [['sin','sci'],['cos','sci'],['tan','sci'],['π','sci']],
      [['ln','sci'],['log','sci'],['1/x','sci'],['e','sci']],
      [['eˣ','sci'],['x²','sci'],['xʸ','sci'],null],
    ],
    secondary:[
      [['⇆','swap'],['Deg/Rad','toggle'],['³√','sci'],['2ˣ','sci']],
      [['sin⁻¹','sci'],['cos⁻¹','sci'],['tan⁻¹','sci'],['x³','sci']],
      [['sinh','sci'],['cosh','sci'],['tanh','sci'],['x!','sci']],
      [['sinh⁻¹','sci'],['cosh⁻¹','sci'],['tanh⁻¹','sci'],null],
    ],
  };
  const renderSciPanel = () => (
    <div style={{
      display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'4px',padding:'4px 10px',backgroundColor:c.bg,flexShrink:0,
      opacity: sciVisible ? 1 : 0,
      transform: sciVisible ? 'translateY(0)' : 'translateY(-8px)',
      transition: 'opacity 300ms cubic-bezier(0.4,0,0.2,1), transform 300ms cubic-bezier(0.4,0,0.2,1)',
    }}>
      {sciRows[sciPanel].flatMap((row,ri)=>row.map((item,ci)=>{
        if (!item) return <div key={`${ri}-${ci}`}/>;
        const [label,type] = item;
        const isSwap = type==='swap';
        const isActive = isSwap && sciPanel==='secondary';
        return (
          <button key={`${ri}-${ci}`} onMouseDown={MD}
            onClick={vib(()=>{
              if(type==='swap')setSciPanel(p=>p==='primary'?'secondary':'primary');
              else if(type==='toggle')setAngleMode(m=>m==='deg'?'rad':'deg');
              else if(type==='pct')handlePercent();
              else handleSci(label);
            })}
            style={{background:isActive?(theme==='dark'?'#4A4A4C':'#C8C8CA'):c.btnSci,color:c.text,borderRadius:'50px',
              padding:'9px 2px',fontSize:'0.78rem',fontWeight:400,fontFamily:FONT,border:'none',cursor:'pointer',
              textAlign:'center',lineHeight:1}}>
            {type==='toggle'?(angleMode==='deg'?'Deg':'Rad'):label}
          </button>
        );
      }))}
    </div>
  );

  // ── Calculator mode ──
  const isBlank = expression===''&&(result==='0'||result==='');
  const exprLen = expression.length; // raw length — visual has extra spaces from paren-padding
  const exprFontSize = exprLen>28?'1.25rem':exprLen>20?'1.6rem':exprLen>12?'2rem':expression!==''?'2.4rem':'3.2rem';

  const mainBtns = [
    [isBlank?'AC':'C', handleClear,            'fn'],
    ['()',              handleParenthesis,      'fn'],
    ['%',              handlePercent,           'fn'],
    ['÷',              ()=>handleOperator('÷'),'op'],
    [7,()=>handleNumberPress('7'),'num'],[8,()=>handleNumberPress('8'),'num'],[9,()=>handleNumberPress('9'),'num'],
    ['×',()=>handleOperator('×'),'op'],
    [4,()=>handleNumberPress('4'),'num'],[5,()=>handleNumberPress('5'),'num'],[6,()=>handleNumberPress('6'),'num'],
    ['−',()=>handleOperator('-'),'op'],
    [1,()=>handleNumberPress('1'),'num'],[2,()=>handleNumberPress('2'),'num'],[3,()=>handleNumberPress('3'),'num'],
    ['+',()=>handleOperator('+'),'op'],
    ['+/−',handleToggleSign,'num'],[0,()=>handleNumberPress('0'),'num'],['.',handleDecimal,'num'],
    ['=',handleEquals,'eq'],
  ];

  const histRightBtns = [
    ['÷',()=>handleOperator('÷'),false],
    ['×',()=>handleOperator('×'),false],
    ['−',()=>handleOperator('-'),false],
    ['+',()=>handleOperator('+'),false],
    ['=',handleEquals,true],
  ];

  const renderCalcMode = () => (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',minHeight:0,position:'relative',backgroundColor:c.bg}}>
      {toast && (
        <div key={toast.id} style={{
          position:'absolute', bottom:'calc(40% + 12px)', left:'50%', transform:'translateX(-50%)',
          backgroundColor:'rgba(58,58,60,0.97)', color:'#fff', padding:'10px 22px', borderRadius:'50px',
          fontSize:'0.95rem', fontWeight:500, fontFamily:FONT, whiteSpace:'nowrap', zIndex:100, pointerEvents:'none',
          animation:'toastIn 0.28s cubic-bezier(0.34,1.56,0.64,1)',
        }}>
          {toast.msg}
        </div>
      )}

      {/* Native Long-Press Tooltip with Edge Protection */}
      {btnTooltip && (
        <div style={{
          position: 'fixed', left: btnTooltip.x, top: btnTooltip.y,
          transform: `translate(${btnTooltip.txAlign}, -100%)`, 
          zIndex: 9999, backgroundColor: theme === 'dark' ? '#3A3A3C' : '#5A5A5C',
          color: '#FFFFFF', padding: '6px 14px', borderRadius: '50px',
          fontSize: '0.85rem', fontFamily: FONT, pointerEvents: 'none',
          whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          animation: 'fadeIn 0.15s ease-out'
        }}>
          {btnTooltip.text}
        </div>
      )}

      {/* Display Area */}
      <div style={{flex:'1 1 0',minHeight:0,padding:'16px 20px 8px',display:'flex',
        flexDirection:'column',justifyContent:'flex-end',backgroundColor:c.bg,overflow:'hidden',position:'relative'}}>
        
        {angleMode === 'rad' && (
          <div style={{position:'absolute',top:0,left:0,padding:'4px 8px', color:c.textSecondary,fontSize:'0.8rem',fontFamily:FONT,fontWeight:500, userSelect:'none',pointerEvents:'none'}}>
            Rad
          </div>
        )}
        
        <div ref={inputRef} onClick={handleDisplayClick}
          style={{
            fontSize: visualExpression.length > 25 ? '1.6rem' : visualExpression.length > 18 ? '2.2rem' : visualExpression.length > 11 ? '2.8rem' : expression !== '' ? '3.6rem' : '0',
            color:c.exprText, background:'transparent', textAlign:'right', width:'100%', fontWeight:300, lineHeight:1.2, cursor:'text',
            fontFamily:FONT, padding:0, margin:0, wordBreak:'normal', overflowWrap:'break-word', userSelect:'auto', WebkitUserSelect:'auto',
            overflowY:'auto', maxHeight:'calc(100% - 60px)', scrollbarWidth:'none', marginBottom: expression ? '4px' : '0', minHeight: expression ? '1em' : '0'
          }}>
          {expression && renderColoredExpr(visualExpression, cursorVisPos, c.exprText, c.btnEq, c.accent)}
        </div>
        
        <div style={{
          fontSize: expression && livePreview ? '1.8rem' : (result.length>14?'2rem':result.length>10?'2.8rem':result.length>6?'3.4rem':'4rem'),
          fontWeight:300, textAlign:'right', color:c.textSecondary, fontFamily:FONT, letterSpacing:'-1px', lineHeight:1.1, wordBreak:'break-all'
        }}>
          {expression && livePreview ? formatNumber(livePreview) : formatNumber(result)}
        </div>
      </div>

      {renderSamsungToolbar(<button onMouseDown={MD} onClick={handleBackspace} style={iconBtn(false)}><BackspaceIcon size={22}/></button>)}

      {/* ─── DYNAMIC LAYOUT: TABLET vs MOBILE ─── */}
      {isTablet ? (
        /* --- TABLET SPLIT-SCREEN LAYOUT --- */
        /* FIXED: Added padding and gap to the parent, removed from children to unify the grid */
        <div style={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden', backgroundColor: c.bg, padding: '16px', gap: '8px' }}>
          
          {/* LEFT PANEL: History OR Scientific */}
          {/* FIXED: Changed flex to 3 (to represent 3 columns) and removed the right border! */}
          <div style={{ flex: 3, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {showHistory ? (
              <div style={{ flex: 1, overflowY: 'auto', padding: '0 0 80px 0', position: 'relative' }}>
                {history.length === 0 ? (
                  <div style={{textAlign:'center',marginTop:'40px',color:c.textSecondary,fontFamily:FONT,fontSize:'0.9rem'}}>No history yet</div>
                ) : history.map((item, i) => {
                  const eq = item.lastIndexOf(' = ');
                  const expr = eq >= 0 ? item.slice(0, eq) : item, res = eq >= 0 ? item.slice(eq + 3) : '';
                  return (
                    <div key={i} style={{width:'100%', padding:'12px 24px', textAlign:'right', display:'flex', flexDirection:'column', alignItems:'flex-end'}}>
                      <motion.div whileTap={{ scale: 0.95, opacity: 0.6 }} onClick={()=>{ const rawExpr = expr.replace(/,/g, ''); setExpression(rawExpr); setResult('0'); setCursorPos(rawExpr.length); setShowHistory(false); }} style={{color:c.textSecondary, fontSize:'1rem', fontFamily:FONT, marginBottom:'6px', cursor:'pointer', padding:'4px 0'}}>{expr}</motion.div>
                      <motion.div whileTap={{ scale: 0.95, opacity: 0.6 }} onClick={()=>{ setExpression(''); setResult(res.replace(/,/g, '')); setCursorPos(null); setShowHistory(false); }} style={{color:c.text, fontSize:'1.3rem', fontFamily:FONT, fontWeight:400, cursor:'pointer', padding:'4px 0'}}>=&thinsp;{res}</motion.div>
                    </div>
                  );
                })}
                {history.length > 0 && (
                  <div style={{position:'absolute', bottom:'0px', left:'50%', transform:'translateX(-50%)', zIndex:10}}>
                    <button onClick={()=>setHistory([])} style={{padding:'12px 24px',borderRadius:'50px',border:'none', background:theme==='dark'?'#3A3A3C':'#D8D8D8', color:c.text,fontFamily:FONT,fontWeight:600,fontSize:'0.95rem',cursor:'pointer', boxShadow:'0 4px 12px rgba(0,0,0,0.2)'}}>Clear history</button>
                  </div>
                )}
              </div>
            ) : (
              /* FIXED: Removed internal padding so it sits flush with the gap */
              <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gridTemplateRows: 'repeat(5,1fr)', gap: '8px', padding: 0, backgroundColor: c.bg }}>
                {(sciPanel === 'primary' ? [
                  ['⇆','swap'],['Deg/Rad','toggle'],['√','sci'],
                  ['sin','sci'],['cos','sci'],['tan','sci'],
                  ['ln','sci'],['log','sci'],['1/x','sci'],
                  ['eˣ','sci'],['x²','sci'],['xʸ','sci'],
                  ['|x|','sci'],['π','sci'],['e','sci']
                ] : [
                  ['⇆','swap'],['Deg/Rad','toggle'],['³√','sci'],
                  ['sin⁻¹','sci'],['cos⁻¹','sci'],['tan⁻¹','sci'],
                  ['sinh','sci'],['cosh','sci'],['tanh','sci'],
                  ['sinh⁻¹','sci'],['cosh⁻¹','sci'],['tanh⁻¹','sci'],
                  ['x³','sci'],['2ˣ','sci'],['x!','sci']
                ]).map((item, i) => {
                  const [label,type] = item;
                  const isActive = (type==='swap' && sciPanel==='secondary') || (type==='toggle' && angleMode==='rad');
                  const dynamicLabel = type === 'toggle' ? (angleMode === 'deg' ? 'Deg' : 'Rad') : label;
                  return (
                    <motion.div key={`sci-tab-${i}`} onContextMenu={(e)=>e.preventDefault()} onPointerDown={(e)=>handleBtnDown(e, dynamicLabel)} onPointerUp={()=>handleBtnUp(()=>{ if(type==='swap')setSciPanel(p=>p==='primary'?'secondary':'primary'); else if(type==='toggle'){setAngleMode(m=>{const n=m==='deg'?'rad':'deg';setToast({id:Date.now(),msg:n==='rad'?'Radians':'Degrees'});setTimeout(()=>setToast(null),2000);return n;});} else if(type==='pct')handlePercent(); else handleSci(label); })} onPointerLeave={handleBtnCancel} onPointerCancel={handleBtnCancel} whileTap="pressed" initial="rest" style={{ display: 'flex', width: '100%', height: '100%', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}>
                      <button style={{ pointerEvents:'none', background:isActive?(theme==='dark'?'#4A4A4C':'#C8C8CA'):c.btnSci, color:c.text, position:'relative', overflow:'hidden', borderRadius:'40px', border:'none', fontFamily:FONT, fontSize:'1.1rem', fontWeight:400, display:'flex', alignItems:'center', justifyContent:'center', width:'100%', height:'100%', lineHeight:1 }}>
                        <motion.div variants={{ pressed: { opacity: 1, transition: { duration: 0 } }, rest: { opacity: 0, transition: { duration: 0.15 } } }} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: theme==='dark'?'rgba(255,255,255,0.15)':'rgba(0,0,0,0.1)' }} />
                        <span style={{ position: 'relative', zIndex: 1 }}>{dynamicLabel}</span>
                      </button>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* RIGHT PANEL: Main Keypad */}
          {/* FIXED: Changed flex to 4 (to represent 4 columns) and removed internal padding */}
          <div style={{ flex: 4, display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gridTemplateRows: 'repeat(5,1fr)', gap: '8px', padding: 0, backgroundColor: c.bg }}>
            {mainBtns.map(([lbl,fn,type],i)=>{
              const bg = type==='eq' ? c.btnEq : c.btnNum;
              const fg = (lbl==='C'||lbl==='AC') ? c.cRed : type==='eq' ? '#FFFFFF' : c.text;
              return (
                <motion.div key={`main-${i}`} onContextMenu={(e)=>e.preventDefault()} onPointerDown={(e)=>handleBtnDown(e, lbl)} onPointerUp={()=>handleBtnUp(fn)} onPointerLeave={handleBtnCancel} onPointerCancel={handleBtnCancel} whileTap="pressed" initial="rest" style={{ display: 'flex', width: '100%', height: '100%', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}>
                  <button style={{ pointerEvents:'none', background:bg, color:fg, position:'relative', overflow:'hidden', borderRadius:'40px', border:'none', fontSize:'1.5rem', fontWeight:'400', fontFamily:FONT, display:'flex', alignItems:'center', justifyContent:'center', width:'100%', height:'100%', lineHeight:1 }}>
                    <motion.div variants={{ pressed: { opacity: 1, transition: { duration: 0 } }, rest: { opacity: 0, transition: { duration: 0.15 } } }} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: theme==='dark'?'rgba(255,255,255,0.15)':'rgba(0,0,0,0.1)' }} />
                    <span style={{ position: 'relative', zIndex: 1 }}>{lbl}</span>
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>

      ) : (

        /* --- MOBILE ORIGINAL LAYOUT --- */
        showHistory ? (
          <div style={{flex:'0 0 60%',display:'flex',overflow:'hidden',minHeight:0,backgroundColor:c.bg, position:'relative'}}>
            <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
              <div style={{flex:1,overflowY:'auto',padding:'4px 0 80px 0'}}>
                {history.length===0?(
                  <div style={{textAlign:'center',marginTop:'40px',color:c.textSecondary,fontFamily:FONT,fontSize:'0.9rem'}}>No history yet</div>
                ):history.map((item,i)=>{
                  const eq=item.lastIndexOf(' = ');
                  const expr=eq>=0?item.slice(0,eq):item, res=eq>=0?item.slice(eq+3):'';
                  return (
                    <div key={i} style={{width:'100%', padding:'12px 24px', textAlign:'right', display:'flex', flexDirection:'column', alignItems:'flex-end'}}>
                      <motion.div whileTap={{ scale: 0.95, opacity: 0.6 }}
                        onClick={()=>{ 
                          const rawExpr = expr.replace(/,/g, '');
                          setExpression(rawExpr); setResult('0'); setCursorPos(rawExpr.length); setShowHistory(false); 
                        }}
                        style={{color:c.textSecondary, fontSize:'1rem', fontFamily:FONT, marginBottom:'6px', userSelect:'auto', WebkitUserSelect:'auto', cursor:'pointer', padding:'4px 0'}}>
                        {expr}
                      </motion.div>
                      <motion.div whileTap={{ scale: 0.95, opacity: 0.6 }}
                        onClick={()=>{ 
                          setExpression(''); setResult(res.replace(/,/g, '')); setCursorPos(null); setShowHistory(false); 
                        }}
                        style={{color:c.text, fontSize:'1.3rem', fontFamily:FONT, fontWeight:400, userSelect:'auto', WebkitUserSelect:'auto', cursor:'pointer', padding:'4px 0'}}>
                        =&thinsp;{res}
                      </motion.div>
                    </div>
                  );
                })}
              </div>
              {history.length>0&&(
                <div style={{position:'absolute', bottom:'24px', left:'calc(50% - 60px)', transform:'translateX(-50%)', zIndex:10}}>
                  <button onClick={()=>setHistory([])}
                    style={{padding:'12px 24px',borderRadius:'50px',border:'none', background:theme==='dark'?'#3A3A3C':'#D8D8D8',
                      color:c.text,fontFamily:FONT,fontWeight:600,fontSize:'0.95rem',cursor:'pointer', boxShadow:'0 4px 12px rgba(0,0,0,0.2)'}}>
                    Clear history
                  </button>
                </div>
              )}
            </div>
            <div style={{width:'82px',display:'flex',flexDirection:'column',gap:'8px', padding:'8px 10px 8px 6px',backgroundColor:c.bg}}>
              {histRightBtns.map(([lbl,fn,isEq],i)=>(
                <button key={i} onMouseDown={MD} onClick={vib(fn, isEq ? 30 : 10)}
                  style={{background:isEq?c.btnEq:c.btnNum, color:isEq?'#FFFFFF':c.text,borderRadius:'50px',border:'none',
                    cursor:'pointer',flex:1,fontSize:'1rem',fontFamily:FONT,fontWeight:'400', display:'flex',alignItems:'center',justifyContent:'center'}}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div style={{
            flex: sciVisible ? '0 0 62%' : '0 0 auto', display:'flex', flexDirection:'column', overflow:'hidden',
            transition:'flex 250ms cubic-bezier(0.4,0,0.2,1)',
          }}>
            <div style={{
              display:'grid', gridTemplateColumns:'repeat(4,1fr)', gridTemplateRows: sciVisible ? 'repeat(4,1fr)' : undefined,
              gap: 'clamp(4px,1.2vw,7px)', padding: 'clamp(4px,1vh,8px) clamp(8px,2.5vw,14px) clamp(2px,0.5vh,4px)',
              opacity: sciVisible ? 1 : 0, flex: sciVisible ? '4 0 0' : '0 0 0px', minHeight: 0, overflow: 'hidden',
              transition: 'opacity 250ms cubic-bezier(0.4,0,0.2,1), flex 250ms cubic-bezier(0.4,0,0.2,1)', backgroundColor:c.bg,
            }}>
              {showSci && sciRows[sciPanel].flatMap((row,ri)=>row.map((item,ci)=>{
                if (!item) return <div key={`sci-${ri}-${ci}`}/>;
                const [label,type] = item;
                const isActive = (type==='swap' && sciPanel==='secondary') || (type==='toggle' && angleMode==='rad');
                const dynamicLabel = type === 'toggle' ? (angleMode === 'deg' ? 'Deg' : 'Rad') : label;

                return (
                  <motion.div key={`sci-${ri}-${ci}`} 
                    onContextMenu={(e) => e.preventDefault()}
                    onPointerDown={(e) => handleBtnDown(e, dynamicLabel)}
                    onPointerUp={() => handleBtnUp(() => {
                      if(type==='swap') setSciPanel(p=>p==='primary'?'secondary':'primary');
                      else if(type==='toggle') {
                        setAngleMode(m => {
                          const newMode = m === 'deg' ? 'rad' : 'deg';
                          setToast({ id: Date.now(), msg: newMode === 'rad' ? 'Radians' : 'Degrees' });
                          setTimeout(() => setToast(null), 2000);
                          return newMode;
                        });
                      }
                      else if(type==='pct') handlePercent();
                      else handleSci(label);
                    })}
                    onPointerLeave={handleBtnCancel}
                    onPointerCancel={handleBtnCancel}
                    whileTap="pressed" initial="rest"
                    style={{ padding: 'clamp(2px,0.6vw,4px)', display: 'flex', width: '100%', height: '100%', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}>
                    
                    <button style={{
                      pointerEvents: 'none', background:(isActive)?(theme==='dark'?'#4A4A4C':'#C8C8CA'):c.btnSci, color:c.text, 
                      position: 'relative', overflow: 'hidden', borderRadius:'50px', border:'none', 
                      fontFamily:FONT, fontSize:'clamp(0.85rem,4.5vw,1.4rem)', fontWeight:400,
                      display:'flex', alignItems:'center', justifyContent:'center', width:'100%', height:'100%', lineHeight:1, userSelect:'none',
                    }}>
                      <motion.div 
                        variants={{
                          pressed: { opacity: 1, transition: { duration: 0 } },
                          rest: { opacity: 0, transition: { duration: 0.15 } }
                        }}
                        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)' }} 
                      />
                      <span style={{ position: 'relative', zIndex: 1 }}>{dynamicLabel}</span>
                    </button>
                  </motion.div>
                );
              }))}
            </div>

            <div style={{
              display:'grid', gridTemplateColumns:'repeat(4,1fr)', gridTemplateRows: sciVisible ? 'repeat(5,1fr)' : undefined,
              gap: '0px', padding: sciVisible ? 'clamp(0px,0.5vh,4px) clamp(4px,2.5vw,8px) clamp(6px,2vh,12px)' : 'clamp(2px,1vh,6px) clamp(6px,3vw,12px) clamp(6px,2vh,12px)',
              flex: sciVisible ? '5 0 0' : '0 0 auto', minHeight: 0, backgroundColor:c.bg, transition:'padding 250ms cubic-bezier(0.4,0,0.2,1)',
            }}>
              {mainBtns.map(([lbl,fn,type],i)=>{
                const bg = type==='eq' ? c.btnEq : c.btnNum;
                const fg = (lbl==='C'||lbl==='AC') ? c.cRed : type==='eq' ? '#FFFFFF' : c.text;

               return (
                  <motion.div key={i} 
                    onContextMenu={(e) => e.preventDefault()} 
                    onPointerDown={(e) => handleBtnDown(e, lbl)}
                    onPointerUp={() => handleBtnUp(fn)}
                    onPointerLeave={handleBtnCancel}
                    onPointerCancel={handleBtnCancel}
                    whileTap="pressed" initial="rest"
                    style={{ padding: sciVisible ? 'clamp(2px,0.6vw,4px)' : 'clamp(3px,0.9vw,5px)', display: 'flex', width: '100%', height: '100%', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}>
                    
                    <button style={{
                        pointerEvents: 'none', background:bg, color:fg, 
                        position: 'relative', overflow: 'hidden', 
                        borderRadius: sciVisible ? '50px' : '50%', aspectRatio: sciVisible ? undefined : '1', 
                        border:'none', fontSize: sciVisible ? 'clamp(1.1rem,5vw,1.5rem)' : 'clamp(1.5rem,7vw,2.2rem)',
                        fontWeight:'400', fontFamily:FONT, display:'flex', alignItems:'center', justifyContent:'center', 
                        width:'100%', height: '100%', lineHeight:1, userSelect:'none', 
                        transition: 'border-radius 250ms cubic-bezier(0.4,0,0.2,1), font-size 250ms cubic-bezier(0.4,0,0.2,1)',
                      }}>
                      
                      <motion.div 
                        variants={{
                          pressed: { opacity: 1, transition: { duration: 0 } },
                          rest: { opacity: 0, transition: { duration: 0.15 } } 
                        }}
                        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)' }} 
                      />
                      <span style={{ position: 'relative', zIndex: 1 }}>{lbl}</span>
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )
      )}
    </div>
  );

  // ── Custom unit dropdown overlay (floating popover style) ──
  const [cvDropdownOpen, setCvDropdownOpen] = useState(null); // 'from' | 'to' | null
  
  const renderUnitDropdown = (field, currentUnit, units, onSelect) => {
    if (cvDropdownOpen !== field) return null;
    const items = converterType === 'currency' ? POPULAR_CURRENCIES.map(([c,n])=>c) : units;
    return (
      <>
        {/* Invisible overlay to catch outside clicks and close the menu */}
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 199 
        }} onPointerDown={(e) => { e.stopPropagation(); setCvDropdownOpen(null); }} />

        {/* The actual floating dropdown menu */}
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 200,
          marginTop: '6px', minWidth: '220px', maxHeight: '260px',
          backgroundColor: theme === 'dark' ? '#2C2C2E' : '#F2F2F7',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          border: `1px solid ${theme === 'dark' ? '#3A3A3C' : '#D8D8D8'}`,
        }} onPointerDown={e => e.stopPropagation()}>
          
          {/* Scrollable list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
            {items.map((unit) => {
              const isSelected = unit === currentUnit;
              const label = converterType === 'currency'
                ? `${unit} — ${POPULAR_CURRENCIES.find(([c])=>c===unit)?.[1]||unit}`
                : `${unit}${UNIT_SYMBOLS[unit] ? ' ('+UNIT_SYMBOLS[unit]+')' : ''}`;
              
              return (
                <button key={unit} onMouseDown={MD}
                  onClick={() => { onSelect(unit); setCvDropdownOpen(null); }}
                  style={{
                    width: '100%', background: 'none', border: 'none', padding: '12px 16px',
                    textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', fontFamily: FONT, fontSize: '0.95rem',
                    color: isSelected ? '#34C759' : c.text
                  }}>
                  <span style={{ fontWeight: isSelected ? 600 : 400 }}>{label}</span>
                  {isSelected && <span style={{ color: '#34C759', fontSize: '1.05rem' }}>✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      </>
    );
  };

  // ── Tip calculator ──
  const tipAmount = tipSubtotal ? (parseFloat(tipSubtotal)||0) * tipPercent / 100 : 0;
  const tipTotal  = tipSubtotal ? (parseFloat(tipSubtotal)||0) + tipAmount : 0;
  const tipEach   = tipPeople > 0 ? tipTotal / tipPeople : 0;

  // Scroll drum bottom sheet
  const DrumPicker = ({ title, value, min, max, unit, onDone }) => {
    const [local, setLocal] = useState(value);
    const items = Array.from({length:max-min+1},(_,i)=>min+i);
    const listRef = useRef(null);
    useEffect(()=>{
      if(listRef.current){
        const idx = items.indexOf(local);
        listRef.current.scrollTop = idx * 52;
      }
    },[]);
    return (
      <div style={{
        position:'fixed',top:0,left:0,right:0,bottom:0,zIndex:300,
        backgroundColor:'rgba(0,0,0,0.5)',display:'flex',alignItems:'flex-end',
      }} onClick={()=>onDone(local)}>
        <div style={{
          width:'100%',backgroundColor:theme==='dark'?'#2C2C2E':'#F2F2F7',
          borderRadius:'20px 20px 0 0',
        }} onClick={e=>e.stopPropagation()}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',
            padding:'16px 20px 8px',fontFamily:FONT}}>
            <span style={{fontWeight:700,fontSize:'1rem',color:c.text}}>{title}</span>
          </div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'20px',padding:'0 20px'}}>
            <div ref={listRef} style={{
              height:'160px',overflowY:'scroll',scrollSnapType:'y mandatory',
              scrollbarWidth:'none',flex:'0 0 100px',
            }}>
              {items.map(v=>(
                <div key={v} onClick={()=>setLocal(v)} style={{
                  height:'52px',display:'flex',alignItems:'center',justifyContent:'center',
                  scrollSnapAlign:'center',cursor:'pointer',fontFamily:FONT,
                  fontSize: local===v ? '1.6rem' : '1.1rem',
                  fontWeight: local===v ? 700 : 400,
                  color: local===v ? c.text : c.textSecondary,
                  transition:'all 0.1s',
                }}>{String(v).padStart(2,'0')}</div>
              ))}
            </div>
            <span style={{fontFamily:FONT,fontSize:'1.1rem',fontWeight:600,color:c.text}}>{unit}</span>
          </div>
          <div style={{display:'flex',padding:'12px 20px 28px',gap:'12px'}}>
            <button onMouseDown={MD} onClick={()=>onDone(local)}
              style={{flex:1,padding:'16px',borderRadius:'14px',border:'none',background:'#3A3A3C',
                color:'#fff',fontFamily:FONT,fontWeight:600,fontSize:'1rem',cursor:'pointer'}}>
              Cancel
            </button>
            <button onMouseDown={MD} onClick={()=>{ onDone(local); }}
              style={{flex:1,padding:'16px',borderRadius:'14px',border:'none',background:'#4A5240',
                color:'#fff',fontFamily:FONT,fontWeight:600,fontSize:'1rem',cursor:'pointer'}}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderTipCalculator = () => {
    const fmtTip = (v) => {
      if (!v && v!==0) return '';
      const n = typeof v==='number' ? v : parseFloat(v);
      if (isNaN(n)) return '';
      return formatNumber(String(parseFloat(n.toPrecision(10))));
    };
    return (
      <div style={{flex:1,display:'flex',flexDirection:'column',padding:'0 0 8px',backgroundColor:c.bg}}>
        {/* Subtotal row */}
        <div style={{padding:'16px 20px 0',cursor:'pointer'}} onClick={()=>{ setCvActiveField('from'); }}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',
            borderBottom:`1px solid ${c.border}`,paddingBottom:'12px'}}>
            <span style={{color:c.text,fontFamily:FONT,fontSize:'1rem',fontWeight:500}}>Subtotal</span>
            <input type="text" inputMode="none"
              value={tipSubtotal}
              placeholder="0"
              onMouseDown={e=>e.preventDefault()}
              style={{background:'transparent',border:'none',outline:'none',textAlign:'right',
                fontSize:'1.5rem',fontWeight:300,fontFamily:FONT,color:c.text,width:'50%',
                caretColor:c.accent,cursor:'default'}}
            />
          </div>
        </div>

        {/* Tip % row */}
        <div style={{padding:'12px 20px 0'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',
            borderBottom:`1px solid ${c.border}`,paddingBottom:'12px'}}>
            <button onMouseDown={MD} onClick={()=>{ setTipDrumTemp(tipPercent); setTipDrumOpen('tip'); }}
              style={{display:'flex',alignItems:'center',gap:'8px',background:'#2B2B2D',
                border:'none',borderRadius:'50px',padding:'8px 14px',cursor:'pointer',color:c.text,fontFamily:FONT}}>
              <span style={{fontSize:'0.9rem'}}>🪙</span>
              <span style={{fontWeight:600,fontSize:'0.95rem'}}>{tipPercent} %</span>
            </button>
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:'1.5rem',fontWeight:300,fontFamily:FONT,color:c.text}}>
                {tipSubtotal ? fmtTip(tipAmount) : ''}
              </div>
              <div style={{fontSize:'0.8rem',color:c.textSecondary,fontFamily:FONT}}>Tip amount</div>
            </div>
          </div>
        </div>

        {/* Total row */}
        <div style={{padding:'12px 20px 0'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',
            borderBottom:`1px solid ${c.border}`,paddingBottom:'12px'}}>
            <span style={{color:c.text,fontFamily:FONT,fontSize:'1rem',fontWeight:500}}>Total</span>
            <span style={{fontSize:'1.5rem',fontWeight:300,fontFamily:FONT,color:c.text}}>
              {tipSubtotal ? fmtTip(tipTotal) : ''}
            </span>
          </div>
        </div>

        {/* People row + Each */}
        <div style={{padding:'12px 20px 0'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',
            borderBottom:`1px solid ${c.border}`,paddingBottom:'12px'}}>
            <button onMouseDown={MD} onClick={()=>{ setTipDrumTemp(tipPeople); setTipDrumOpen('people'); }}
              style={{display:'flex',alignItems:'center',gap:'8px',background:'#2B2B2D',
                border:'none',borderRadius:'50px',padding:'8px 14px',cursor:'pointer',color:c.text,fontFamily:FONT}}>
              <span style={{fontSize:'0.9rem'}}>👥</span>
              <span style={{fontWeight:600,fontSize:'0.95rem'}}>{tipPeople}</span>
            </button>
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:'1.5rem',fontWeight:300,fontFamily:FONT,color:c.text}}>
                {tipSubtotal && tipPeople > 0 ? fmtTip(tipEach) : ''}
              </div>
              <div style={{fontSize:'0.8rem',color:c.textSecondary,fontFamily:FONT}}>Each</div>
            </div>
          </div>
        </div>

        {/* Keypad for tip subtotal */}
        <div style={{flex:1,display:'grid',gridTemplateColumns:'repeat(4,1fr)',
          gap:'clamp(5px,1.8vw,10px)',
          padding:'clamp(6px,1.5vh,12px) clamp(10px,3vw,16px) clamp(8px,2.5vh,16px)',
          backgroundColor:c.bg}}>
          {[
            [7,()=>setTipSubtotal(v=>v.length>=15?v:v+'7'),'num'],
            [8,()=>setTipSubtotal(v=>v.length>=15?v:v+'8'),'num'],
            [9,()=>setTipSubtotal(v=>v.length>=15?v:v+'9'),'num'],
            [<BackspaceIcon size={20}/>,()=>setTipSubtotal(v=>v.slice(0,-1)),'del'],
            [4,()=>setTipSubtotal(v=>v.length>=15?v:v+'4'),'num'],
            [5,()=>setTipSubtotal(v=>v.length>=15?v:v+'5'),'num'],
            [6,()=>setTipSubtotal(v=>v.length>=15?v:v+'6'),'num'],
            ['C',()=>setTipSubtotal(''),'del'],
            [1,()=>setTipSubtotal(v=>v.length>=15?v:v+'1'),'num'],
            [2,()=>setTipSubtotal(v=>v.length>=15?v:v+'2'),'num'],
            [3,()=>setTipSubtotal(v=>v.length>=15?v:v+'3'),'num'],
            ['↑',()=>{},null],
            ['+/−',()=>setTipSubtotal(v=>v?v.startsWith('-')?v.slice(1):'-'+v:v),'fn'],
            [0,()=>setTipSubtotal(v=>v.length>=15?v:(v==='0'?'0':v+'0')),'num'],
            ['.',()=>setTipSubtotal(v=>v.includes('.')?v:v+'.'),'num'],
            ['↓',()=>{},null],
          ].map(([lbl,fn,type],i)=>{
            const isNull = type===null;
            const bg = isNull ? 'transparent' : c.btnNum;
            const fg = type==='del' ? c.cRed : c.text;
            return (
              <button key={i}
                onMouseDown={(e)=>{ e.preventDefault(); }}
                onClick={()=>{ if(!isNull){vibrate(10);fn();} }}
                style={{background:bg,color:fg,borderRadius:'50%',aspectRatio:'1',
                  border:'none',cursor:isNull?'default':'pointer',
                  fontSize:'clamp(1rem,5.5vw,1.75rem)',fontWeight:300,fontFamily:FONT,
                  display:'flex',alignItems:'center',justifyContent:'center',width:'100%',opacity:isNull?0:1}}>
                {lbl}
              </button>
            );
          })}
        </div>

        {/* Drum pickers */}
        {tipDrumOpen==='tip' && (
          <DrumPicker title="Tip" value={tipPercent} min={1} max={100} unit="%"
            onDone={(v)=>{ setTipPercent(v); setTipDrumOpen(null); }} />
        )}
        {tipDrumOpen==='people' && (
          <DrumPicker title="Split" value={tipPeople} min={1} max={99} unit="People"
            onDone={(v)=>{ setTipPeople(v); setTipDrumOpen(null); }} />
        )}
      </div>
    );
  };

  // ── Converter mode ──
  const convCategories=[
    ['currency','Currency'],
    ['area','Area'],['length','Length'],['temperature','Temperature'],['volume','Volume'],
    ['mass','Mass'],['data','Data'],['speed','Speed'],['time','Time'],
    ['energy','Energy'],['pressure','Pressure'],['angle','Angle'],['power','Power'],['frequency','Frequency'],
    ['tip','Tip'],
  ];

  const renderConverterMode = () => {
    const fromSym=UNIT_SYMBOLS[fromUnit]||'', toSym=UNIT_SYMBOLS[toUnit]||'';
    const isFrom=cvActiveField==='from', isTo=cvActiveField==='to';
    const upActive=cvActiveField==='to', downActive=cvActiveField==='from';
    const fieldLine=(active)=>({borderBottom:`1px solid ${active?(theme==='dark'?'#888':'#888'):c.border}`,padding:'10px 0 8px',cursor:'pointer'});
    const cvBtns=[
      [7,()=>cvPress('7'),'num'],[8,()=>cvPress('8'),'num'],[9,()=>cvPress('9'),'num'],[<BackspaceIcon size={20}/>,cvBackspace,'del'],
      [4,()=>cvPress('4'),'num'],[5,()=>cvPress('5'),'num'],[6,()=>cvPress('6'),'num'],['C',cvClear,'del'],
      [1,()=>cvPress('1'),'num'],[2,()=>cvPress('2'),'num'],[3,()=>cvPress('3'),'num'],['↑',()=>setCvActiveField('from'),upActive?'nav-on':'nav-off'],
      ['+/−',cvToggleSign,'fn'],[0,()=>cvPress('0'),'num'],['.',()=>cvPress('.'),'num'],['↓',()=>setCvActiveField('to'),downActive?'nav-on':'nav-off'],
    ];
    return (
      <div style={{flex:1,display:'flex',flexDirection:'column',backgroundColor:c.bg,overflow:'hidden'}}>
        {/* Back arrow + title — exact Samsung converter header */}
        <div style={{display:'flex',alignItems:'center',padding:'14px 16px 8px',flexShrink:0}}>
          <button onMouseDown={MD} onClick={()=>setMode('calculator')}
            style={{background:'none',border:'none',cursor:'pointer',color:c.text,
              padding:'0 12px 0 0',display:'flex',alignItems:'center',flexShrink:0}}>
            <ChevronLeft size={26}/>
          </button>
          <span style={{fontSize:'1.25rem',fontWeight:700,color:c.text,fontFamily:FONT}}>Unit converter</span>
        </div>

        {/* Category tabs — horizontal scroll */}
        <div style={{display:'flex',gap:'4px',padding:'0 16px 8px',overflowX:'auto',flexShrink:0,scrollbarWidth:'none'}}>
          {convCategories.map(([id,label])=>
            <button key={id} onMouseDown={MD} onClick={()=>handleConverterTypeChange(id)} style={chip(converterType===id)}>{label}</button>
          )}
        </div>
        {/* Separator */}
        <div style={{height:'1px',backgroundColor:c.border,margin:'0 0 4px',flexShrink:0}}/>

        {/* Tip tab — completely different layout */}
        {converterType === 'tip' ? renderTipCalculator() : (<>

        {/* ── From field ── Samsung style: unit name top-left, large value right */}
        <div style={{padding:'8px 20px 0',flexShrink:0}}>
          {/* Unit selector row */}
          <div style={{display:'flex',alignItems:'center',marginBottom:'6px', position: 'relative'}}>
            {converterType === 'currency' ? (
              <button onMouseDown={MD}
                onClick={()=>setCvDropdownOpen(f=>f==='from'?null:'from')}
                style={{background:'none',border:'none',outline:'none',color:c.text,fontFamily:FONT,
                  fontSize:'1rem',fontWeight:400,cursor:'pointer',display:'flex',alignItems:'center',gap:'6px',padding:0}}>
                {fromUnit} — {POPULAR_CURRENCIES.find(([code])=>code===fromUnit)?.[1]||fromUnit}
                <span style={{fontSize:'0.7rem',color:c.textSecondary}}>▼</span>
              </button>
            ) : (
              <button onMouseDown={MD}
                onClick={()=>setCvDropdownOpen(f=>f==='from'?null:'from')}
                style={{background:'none',border:'none',outline:'none',color:c.text,fontFamily:FONT,
                  fontSize:'1rem',fontWeight:400,cursor:'pointer',display:'flex',alignItems:'center',gap:'6px',padding:0}}>
                {optLabel(fromUnit)} <span style={{fontSize:'0.7rem',color:c.textSecondary}}>▼</span>
              </button>
            )}
            {renderUnitDropdown('from', fromUnit, Object.keys(UNITS[converterType]||{}), (u)=>{
              if(converterType==='currency'){setFromUnit(u);setToValue(fromValue?cvConvert(fromValue,u,toUnit):'');}
              else{setFromUnit(u);setFromValue('');setToValue('');}
            })}
          </div>
          {/* Value display row */}
          <div style={{display:'flex',alignItems:'baseline',justifyContent:'flex-end',gap:'8px',
            borderBottom:`1px solid ${isFrom ? (theme==='dark'?'#666':'#999') : c.border}`,paddingBottom:'12px'}}>
            <input ref={fromInputRef} type="text" inputMode="none"
              value={cvFormat(fromValue)} placeholder="0"
              onMouseDown={e=>{ e.preventDefault(); setCvActiveField('from'); fromInputRef.current?.focus(); }}
              onFocus={()=>{ setCvActiveField('from'); }}
              onChange={e=>{ cvHandleChange('from',e); }}
              style={{fontSize:'3rem',fontWeight:300,color:isFrom?c.text:c.textSecondary,fontFamily:FONT,
                background:'transparent',
                border:'none',outline:'none',
                textAlign:'right',flex:1,caretColor:'#007AFF',letterSpacing:'-1px',padding:'0 4px',margin:0,borderRadius:4}}
            />
            <span style={{color:isFrom?c.text:c.textSecondary,fontSize:'1.2rem',fontWeight:400,fontFamily:FONT,flexShrink:0,marginBottom:'4px'}}>
              {converterType === 'currency' ? fromUnit : fromSym}
            </span>
          </div>
        </div>

        {/* ── To field ── */}
        <div style={{padding:'8px 20px 0',flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',marginBottom:'6px', position: 'relative'}}>
            {converterType === 'currency' ? (
              <button onMouseDown={MD}
                onClick={()=>setCvDropdownOpen(f=>f==='to'?null:'to')}
                style={{background:'none',border:'none',outline:'none',color:c.text,fontFamily:FONT,
                  fontSize:'1rem',fontWeight:400,cursor:'pointer',display:'flex',alignItems:'center',gap:'6px',padding:0}}>
                {toUnit} — {POPULAR_CURRENCIES.find(([code])=>code===toUnit)?.[1]||toUnit}
                <span style={{fontSize:'0.7rem',color:c.textSecondary}}>▼</span>
              </button>
            ) : (
              <button onMouseDown={MD}
                onClick={()=>setCvDropdownOpen(f=>f==='to'?null:'to')}
                style={{background:'none',border:'none',outline:'none',color:c.text,fontFamily:FONT,
                  fontSize:'1rem',fontWeight:400,cursor:'pointer',display:'flex',alignItems:'center',gap:'6px',padding:0}}>
                {optLabel(toUnit)} <span style={{fontSize:'0.7rem',color:c.textSecondary}}>▼</span>
              </button>
            )}
            {renderUnitDropdown('to', toUnit, Object.keys(UNITS[converterType]||{}), (u)=>{
              if(converterType==='currency'){setToUnit(u);setToValue(fromValue?cvConvert(fromValue,fromUnit,u):'');}
              else{setToUnit(u);setToValue(fromValue?cvConvert(fromValue,fromUnit,u):'');}
            })}
          </div>
          <div style={{display:'flex',alignItems:'baseline',justifyContent:'flex-end',gap:'8px',
            borderBottom:`1px solid ${isTo ? (theme==='dark'?'#666':'#999') : c.border}`,paddingBottom:'12px'}}>
            <input ref={toInputRef} type="text" inputMode="none"
              value={cvFormat(toValue)} placeholder="0"
              onMouseDown={e=>{ e.preventDefault(); setCvActiveField('to'); toInputRef.current?.focus(); }}
              onFocus={()=>{ setCvActiveField('to'); }}
              onChange={e=>{ cvHandleChange('to',e); }}
              style={{fontSize:'3rem',fontWeight:300,color:isTo?c.text:c.textSecondary,fontFamily:FONT,
                background:'transparent',
                border:'none',outline:'none',
                textAlign:'right',flex:1,caretColor:'#007AFF',letterSpacing:'-1px',padding:'0 4px',margin:0,borderRadius:4}}
            />
            <span style={{color:isTo?c.text:c.textSecondary,fontSize:'1.2rem',fontWeight:400,fontFamily:FONT,flexShrink:0,marginBottom:'4px'}}>
              {converterType === 'currency' ? toUnit : toSym}
            </span>
          </div>
        </div>

        {converterType === 'currency' && (
          <div style={{padding:'4px 20px 2px',flexShrink:0}}>
            {fxStatus === 'loading' && (
              <span style={{fontSize:'0.72rem',color:c.textSecondary,fontFamily:FONT}}>
                ⏳ Fetching live rates…
              </span>
            )}
            {fxStatus === 'live' && fxDate && (
              <span style={{fontSize:'0.72rem',color:'#34C759',fontFamily:FONT}}>
                🟢 Live rates · {new Date(fxDate+'T00:00:00Z').toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric',timeZone:'UTC'})}
              </span>
            )}
            {fxStatus === 'cached' && fxDate && (
              <span style={{fontSize:'0.72rem',color:c.textSecondary,fontFamily:FONT}}>
                {isOnline ? '📦' : '📡'} {isOnline ? 'Cached' : 'Offline · cached'} rates · {new Date(fxDate+'T00:00:00Z').toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric',timeZone:'UTC'})}
              </span>
            )}
            {fxStatus === 'stale' && fxDate && (
              <span style={{fontSize:'0.72rem',color:'#FF9F0A',fontFamily:FONT}}>
                {isOnline ? '🔄 Updating… using cached' : '📡 Offline · cached'} · {new Date(fxDate+'T00:00:00Z').toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric',timeZone:'UTC'})}
              </span>
            )}
            {fxStatus === 'offline' && fxDate && (
              <span style={{fontSize:'0.72rem',color:'#FF9F0A',fontFamily:FONT}}>
                📡 {isOnline ? 'Fetching…' : 'Offline'} · built-in rates from {new Date(fxDate+'T00:00:00Z').toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric',timeZone:'UTC'})}
              </span>
            )}
          </div>
        )}
        {showSettings&&renderSettings()}

        {/* Keypad — circles, Samsung layout: 7,8,9,⌫ / 4,5,6,C / 1,2,3,↑ / +/-,0,.,↓ */}
        <div style={{flex:1,display:'grid',gridTemplateColumns:'repeat(4,1fr)',
          gap:'clamp(4px,1.5vw,8px)',
          padding:'clamp(4px,1vh,10px) clamp(4px,1.5vw,10px) clamp(6px,2vh,12px)',
          backgroundColor:c.bg, overflow:'hidden'}}>
          {cvBtns.map(([lbl,fn,type],i)=>{
            // All circles; C is red text; nav arrows same dark bg
            const bg = c.btnNum;
            const fg = type==='del' ? c.cRed : (lbl==='C') ? c.cRed : (type==='nav-off') ? c.textSecondary : c.text;
            return (
              <button key={i}
                onMouseDown={(e)=>{ e.preventDefault(); }}
                onClick={()=>{ vibrate(10); fn(); }}
                style={{background:bg, color:fg,
                  /* FIXED: Perfect circles on mobile, sleek rounded rectangles on PC */
                  borderRadius: isTablet ? '20px' : '50%', 
                  aspectRatio: isTablet ? 'auto' : '1',
                  minHeight: isTablet ? '65px' : 'auto',
                  border:'none', cursor:'pointer',
                  fontSize:'clamp(1rem,5vw,1.6rem)', fontWeight:'300', fontFamily:FONT,
                  display:'flex', alignItems:'center', justifyContent:'center', width:'100%',
                  minWidth:0,
                }}>
                {lbl}
              </button>
            );
          })}
        </div>
        </>)}
      </div>
    );
  };

  // ── Financial mode ──
  const renderEMI      = () => (<div style={{padding:'16px'}}>
    <h3 style={{color:c.text,fontFamily:FONT,fontSize:'1.05rem',fontWeight:600,marginBottom:'16px',marginTop:0}}>Loan EMI Calculator</h3>
    <InputField colors={c} label="Loan Amount" unit="₹" value={principal} onChange={setPrincipal} placeholder="Enter amount"/>
    <InputField colors={c} label="Annual Interest Rate" unit="%" value={rate} onChange={setRate} placeholder="e.g. 8.5"/>
    {dateModeToggle(emiDateMode,setEmiDateMode)}
    {emiDateMode==='date'
      ?<DateRangeField fromDate={emiFromDate} setFromDate={setEmiFromDate} toDate={emiToDate} setToDate={setEmiToDate}/>
      :tenureRow(tenureYears,setTenureYears,tenureMonths,setTenureMonths,tenureDays,setTenureDays)}
    {calcActionBtn(calculateEMI,'Calculate EMI')}
    {emiResult&&<ResultCard colors={c} data={[{label:'Monthly EMI',value:`₹${formatNumber(emiResult.emi)}`,highlight:true},{label:'Principal',value:`₹${formatNumber(emiResult.principal)}`},{label:'Total Interest',value:`₹${formatNumber(emiResult.totalInterest)}`},{label:'Total Amount',value:`₹${formatNumber(emiResult.totalAmount)}`}]}/>}
  </div>);

  const renderSIP      = () => (<div style={{padding:'16px'}}>
    <h3 style={{color:c.text,fontFamily:FONT,fontSize:'1.05rem',fontWeight:600,marginBottom:'16px',marginTop:0}}>SIP Calculator</h3>
    <InputField colors={c} label="Monthly Investment" unit="₹" value={sipAmount} onChange={setSipAmount} placeholder="Enter amount"/>
    <InputField colors={c} label="Expected Annual Return" unit="%" value={sipRate} onChange={setSipRate} placeholder="e.g. 12"/>
    {dateModeToggle(sipDateMode,setSipDateMode)}
    {sipDateMode==='date'
      ?<DateRangeField fromDate={sipFromDate} setFromDate={setSipFromDate} toDate={sipToDate} setToDate={setSipToDate}/>
      :<InputField colors={c} label="Time Period" unit="yr" value={sipYears} onChange={setSipYears} placeholder="Enter years"/>}
    {calcActionBtn(calculateSIP,'Calculate SIP')}
    {sipResult&&<ResultCard colors={c} data={[{label:'Future Value',value:`₹${formatNumber(sipResult.futureValue)}`,highlight:true},{label:'Amount Invested',value:`₹${formatNumber(sipResult.invested)}`},{label:'Est. Returns',value:`₹${formatNumber(sipResult.returns)}`}]}/>}
  </div>);

  const renderRD = () => (<div style={{padding:'16px'}}>
    <h3 style={{color:c.text,fontFamily:FONT,fontSize:'1.05rem',fontWeight:600,marginBottom:'16px',marginTop:0}}>Recurring Deposit Calculator</h3>
    <InputField colors={c} label="Monthly Deposit" unit="₹" value={rdAmount} onChange={setRdAmount} placeholder="Enter monthly amount"/>
    <InputField colors={c} label="Annual Interest Rate" unit="%" value={rdRate} onChange={setRdRate} placeholder="e.g. 7.5"/>
    {/* Compounding Frequency */}
    <div style={{marginBottom:'12px'}}>
      <div style={{color:c.textSecondary,fontSize:'0.8rem',marginBottom:'6px'}}>Compounding Frequency</div>
      <div style={{display:'flex',gap:'6px',flexWrap:'wrap'}}>
        {['monthly','quarterly','half-yearly','yearly'].map(f=>(
          <button key={f} onClick={()=>setRdFreq(f)}
            style={{padding:'6px 12px',borderRadius:'50px',border:'none',cursor:'pointer',fontSize:'0.78rem',fontFamily:FONT,
              background:rdFreq===f?c.btnEq:c.btnSci,color:rdFreq===f?'#fff':c.text,fontWeight:rdFreq===f?600:400}}>
            {f.charAt(0).toUpperCase()+f.slice(1)}
          </button>
        ))}
      </div>
    </div>
    {/* Tenure mode toggle */}
    {dateModeToggle(rdDateMode, setRdDateMode)}
    {rdDateMode==='date'
      ? <DateRangeField fromDate={rdFromDate} setFromDate={setRdFromDate} toDate={rdToDate} setToDate={setRdToDate}/>
      : tenureRow(rdYears, setRdYears, rdMonthsPart, setRdMonthsPart, rdDaysPart, setRdDaysPart)
    }
    {calcActionBtn(calculateRD,'Calculate RD')}
    {rdResult&&<ResultCard colors={c} data={[
      {label:'Maturity Amount',value:`₹${formatNumber(rdResult.maturity)}`,highlight:true},
      {label:'Amount Deposited',value:`₹${formatNumber(rdResult.invested)}`},
      {label:'Interest Earned',value:`₹${formatNumber(rdResult.interest)}`},
      {label:'Tenure',value:`${rdResult.months} months`},
    ]}/>}
  </div>);

  const renderInterest = () => (<div style={{padding:'16px'}}>
    <h3 style={{color:c.text,fontFamily:FONT,fontSize:'1.05rem',fontWeight:600,marginBottom:'16px',marginTop:0}}>Interest Calculator</h3>
    <div style={{display:'flex',gap:'8px',marginBottom:'16px'}}>
      {[['simple','Simple'],['compound','Compound']].map(([id,lbl])=><button key={id} onClick={()=>setInterestType(id)} style={finChip(interestType===id)}>{lbl}</button>)}
    </div>
    <InputField colors={c} label="Principal Amount" unit="₹" value={interestPrincipal} onChange={setInterestPrincipal} placeholder="Enter principal"/>
    <InputField colors={c} label="Interest Rate" unit="% p.a." value={interestRate} onChange={setInterestRate} placeholder="Enter rate"/>
    {interestType==='compound'&&(<div style={{marginBottom:'14px'}}>
      <label style={{display:'block',fontSize:'0.75rem',marginBottom:'6px',fontWeight:500,color:c.textSecondary,fontFamily:FONT}}>Compounding Frequency</label>
      <select className={selCls} value={compoundFrequency} onChange={e=>setCompoundFrequency(e.target.value)} style={{width:'100%',padding:'12px',borderRadius:'12px',outline:'none',fontWeight:500,backgroundColor:c.inputBg,color:c.text,border:`1px solid ${c.border}`,fontFamily:FONT,fontSize:'1rem'}}>
        {['annually','semi-annually','quarterly','monthly','daily'].map(f=><option key={f} value={f} style={{background:c.inputBg}}>{f.charAt(0).toUpperCase()+f.slice(1)}</option>)}
      </select>
    </div>)}
    {dateModeToggle(intDateMode,setIntDateMode)}
    {intDateMode==='date'
      ?<DateRangeField fromDate={intFromDate} setFromDate={setIntFromDate} toDate={intToDate} setToDate={setIntToDate}/>
      :tenureRow(interestYears,setInterestYears,interestMonths,setInterestMonths,interestDays,setInterestDays)}
    {calcActionBtn(calculateInterest,'Calculate')}
    {interestResult&&<ResultCard colors={c} data={[{label:'Interest Earned',value:`₹${formatNumber(interestResult.interest)}`,highlight:true},{label:'Principal',value:`₹${formatNumber(interestResult.principal)}`},{label:'Total Amount',value:`₹${formatNumber(interestResult.totalAmount)}`}]}/>}
  </div>);

  const renderDiscount = () => (<div style={{padding:'16px'}}>
    <h3 style={{color:c.text,fontFamily:FONT,fontSize:'1.05rem',fontWeight:600,marginBottom:'16px',marginTop:0}}>Discount Calculator</h3>
    <div style={{display:'flex',gap:'6px',marginBottom:'16px',flexWrap:'wrap'}}>
      {[['forward','Price → Final'],['reverse','Final → Price'],['find','Find %']].map(([m,lbl])=><button key={m} onClick={()=>{setDiscountMode(m);setDiscountResult(null);}} style={finChip(discountMode===m)}>{lbl}</button>)}
    </div>
    {discountMode==='forward'&&<InputField colors={c} label="Original Price" unit="₹" value={originalPrice} onChange={setOriginalPrice} placeholder="e.g. 1000"/>}
    {discountMode==='reverse'&&<InputField colors={c} label="Final Price Paid" unit="₹" value={discountFinalPrice} onChange={setDiscountFinalPrice} placeholder="e.g. 799"/>}
    {discountMode==='find'&&<><InputField colors={c} label="Original / MRP" unit="₹" value={originalPrice} onChange={setOriginalPrice} placeholder="e.g. 1000"/><InputField colors={c} label="Final Price Paid" unit="₹" value={discountFinalPrice} onChange={setDiscountFinalPrice} placeholder="e.g. 799"/></>}
    {discountMode!=='find'&&<InputField colors={c} label="Discount" unit="%" value={discountPercent} onChange={setDiscountPercent} placeholder="e.g. 20"/>}
    {calcActionBtn(calculateDiscount,'Calculate')}
    {discountResult&&<ResultCard colors={c} data={[{label:'Original / MRP',value:`₹${formatNumber(discountResult.originalPrice)}`},{label:discountResult.mode==='find'?'Discount %':'Discount',value:discountResult.mode==='find'?discountResult.savings:`₹${formatNumber(discountResult.discountAmount)} (${discountResult.savings})`,highlight:true},{label:discountResult.mode==='find'?'Amount Saved':'Final Price',value:discountResult.mode==='find'?`₹${formatNumber(discountResult.discountAmount)}`:`₹${formatNumber(discountResult.finalPrice)}`,highlight:discountResult.mode==='find'}]}/>}
  </div>);

  const renderGST      = () => (<div style={{padding:'16px'}}>
    <h3 style={{color:c.text,fontFamily:FONT,fontSize:'1.05rem',fontWeight:600,marginBottom:'16px',marginTop:0}}>GST Calculator</h3>
    <div style={{display:'flex',gap:'8px',marginBottom:'16px'}}>
      {[['add','➕ Add GST'],['remove','➖ Remove GST']].map(([id,lbl])=><button key={id} onClick={()=>{setGstType(id);setGstResult(null);}} style={finChip(gstType===id)}>{lbl}</button>)}
    </div>
    <InputField colors={c} label={gstType==='add'?'Base Amount':'Amount incl. GST'} unit="₹" value={gstAmount} onChange={setGstAmount} placeholder="Enter amount"/>
    <InputField colors={c} label="GST Rate" unit="%" value={gstRate} onChange={setGstRate} placeholder="e.g. 5, 12, 18, 28"/>
    {calcActionBtn(calculateGST,'Calculate GST')}
    {gstResult&&<ResultCard colors={c} data={[{label:gstResult.type==='add'?'Base Amount':'Total (incl. GST)',value:`₹${formatNumber(gstResult.type==='add'?gstResult.baseAmount:gstResult.totalAmount)}`},{label:`GST @ ${gstResult.rate}`,value:`₹${formatNumber(gstResult.gstAmount)}`,highlight:true},{label:'CGST (50%)',value:`₹${formatNumber(gstResult.cgst)}`},{label:'SGST (50%)',value:`₹${formatNumber(gstResult.sgst)}`},{label:gstResult.type==='add'?'Total with GST':'Base Amount (excl. GST)',value:`₹${formatNumber(gstResult.type==='add'?gstResult.totalAmount:gstResult.baseAmount)}`,highlight:true}]}/>}
  </div>);

  const renderMargin   = () => (<div style={{padding:'16px'}}>
    <h3 style={{color:c.text,fontFamily:FONT,fontSize:'1.05rem',fontWeight:600,marginBottom:'16px',marginTop:0}}>Profit / Loss Calculator</h3>
    <InputField colors={c} label="Cost Price" unit="₹" value={costPrice} onChange={setCostPrice} placeholder="Enter cost"/>
    <InputField colors={c} label="Selling Price" unit="₹" value={sellingPrice} onChange={setSellingPrice} placeholder="Enter selling price"/>
    {calcActionBtn(calculateMargin,'Calculate')}
    {marginResult&&<ResultCard colors={c} data={[{label:'Status',value:marginResult.status,highlight:true},{label:`${marginResult.status} Amount`,value:`₹${formatNumber(marginResult.profit)}`},{label:`${marginResult.status} %`,value:`${formatNumber(marginResult.profitPercent)}%`},{label:'Margin %',value:`${formatNumber(marginResult.marginPercent)}%`}]}/>}
  </div>);

  const renderROI      = () => (<div style={{padding:'16px'}}>
    <h3 style={{color:c.text,fontFamily:FONT,fontSize:'1.05rem',fontWeight:600,marginBottom:'16px',marginTop:0}}>ROI Calculator</h3>
    <InputField colors={c} label="Initial Investment" unit="₹" value={initialInvestment} onChange={setInitialInvestment} placeholder="Enter initial amount"/>
    <InputField colors={c} label="Final Value" unit="₹" value={finalValue} onChange={setFinalValue} placeholder="Enter final value"/>
    {dateModeToggle(roiDateMode,setRoiDateMode)}
    {roiDateMode==='date'
      ?<DateRangeField fromDate={roiFromDate} setFromDate={setRoiFromDate} toDate={roiToDate} setToDate={setRoiToDate}/>
      :<InputField colors={c} label="Time Period (Optional)" unit="yr" value={roiYears} onChange={setRoiYears} placeholder="Enter years"/>}
    {calcActionBtn(calculateROI,'Calculate ROI')}
    {roiResult&&<ResultCard colors={c} data={[{label:'ROI',value:roiResult.roiPercent==='N/A'?'N/A':`${formatNumber(roiResult.roiPercent)}%`,highlight:true},{label:'Total Return',value:`₹${formatNumber(roiResult.totalReturn)}`},...(roiResult.annualizedReturn?[{label:'Annualized Return',value:`${formatNumber(roiResult.annualizedReturn)}%`}]:[])]}/>}
  </div>);

  const renderGratuity = () => (<div style={{padding:'16px'}}>
    <h3 style={{color:c.text,fontFamily:FONT,fontSize:'1.05rem',fontWeight:600,marginBottom:'16px',marginTop:0}}>Gratuity Calculator (India)</h3>
    <InputField colors={c} label="Last Drawn Monthly Salary" unit="₹" value={lastSalary} onChange={setLastSalary} placeholder="Enter salary"/>
    <InputField colors={c} label="Years of Service" unit="yr" value={yearsOfService} onChange={setYearsOfService} placeholder="Enter years"/>
    {calcActionBtn(calculateGratuity,'Calculate Gratuity')}
    {gratuityResult&&<ResultCard colors={c} data={[{label:'Gratuity Amount',value:`₹${formatNumber(gratuityResult.gratuity)}`,highlight:true},{label:'Years of Service',value:`${gratuityResult.yearsOfService} yr`},{label:'Last Salary',value:`₹${formatNumber(gratuityResult.lastSalary)}`}]}/>}
  </div>);

  const renderFuel     = () => (<div style={{padding:'16px'}}>
    <h3 style={{color:c.text,fontFamily:FONT,fontSize:'1.05rem',fontWeight:600,marginBottom:'16px',marginTop:0}}>Fuel Economy</h3>
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px',marginBottom:'14px'}}>
      {[['Distance Unit',distanceUnit,setDistanceUnit,[['km','Kilometer (km)'],['mile','Mile (mi)']]],
        ['Fuel Unit',fuelUnit,setFuelUnit,[['liter','Liter (L)'],['gallon','Gallon (gal)']]]].map(([lbl,val,setter,opts])=>(
        <div key={lbl}>
          <label style={{display:'block',fontSize:'0.75rem',marginBottom:'6px',color:c.textSecondary,fontFamily:FONT,fontWeight:500}}>{lbl}</label>
          <select className={selCls} value={val} onChange={e=>setter(e.target.value)} style={{width:'100%',padding:'10px',borderRadius:'12px',outline:'none',fontWeight:500,backgroundColor:c.inputBg,color:c.text,border:`1px solid ${c.border}`,fontFamily:FONT}}>
            {opts.map(([v,l])=><option key={v} value={v} style={{background:c.inputBg}}>{l}</option>)}
          </select>
        </div>
      ))}
    </div>
    <InputField colors={c} label="Distance" unit={distanceUnit==='km'?'km':'mi'} value={distance} onChange={setDistance} placeholder="Enter distance"/>
    <InputField colors={c} label="Fuel Used" unit={fuelUnit==='liter'?'L':'gal'} value={fuelUsed} onChange={setFuelUsed} placeholder="Enter fuel amount"/>
    <InputField colors={c} label="Fuel Price (Optional)" unit="₹/L" value={fuelPrice} onChange={setFuelPrice} placeholder="Price per liter"/>
    {calcActionBtn(calculateFuelEconomy,'Calculate')}
    {fuelEconomyResult&&<ResultCard colors={c} data={[{label:'Fuel Economy',value:`${formatNumber(fuelEconomyResult.kmPerLiter)} km/L`,highlight:true},{label:'Fuel Usage',value:`${formatNumber(fuelEconomyResult.litersPer100km)} L/100km`},{label:'MPG',value:formatNumber(fuelEconomyResult.mpg)},...(fuelEconomyResult.totalCost?[{label:'Total Fuel Cost',value:`₹${formatNumber(fuelEconomyResult.totalCost)}`},{label:'Cost per km',value:`₹${formatNumber(fuelEconomyResult.costPerKm)}`}]:[])]}/>}
  </div>);

  const finTabs=[
    {id:'emi',label:'EMI',icon:DollarSign,render:renderEMI},
    {id:'sip',label:'SIP',icon:TrendingUp,render:renderSIP},
    {id:'rd',label:'RD',icon:Wallet,render:renderRD},
    {id:'interest',label:'Interest',icon:Percent,render:renderInterest},
    {id:'discount',label:'Discount',icon:Percent,render:renderDiscount},
    {id:'margin',label:'Profit',icon:Wallet,render:renderMargin},
    {id:'roi',label:'ROI',icon:TrendingUp,render:renderROI},
    {id:'gratuity',label:'Gratuity',icon:Award,render:renderGratuity},
    {id:'fuel',label:'Fuel',icon:Fuel,render:renderFuel},
    {id:'gst',label:'GST',icon:Hash,render:renderGST},
  ];

  const renderFinancialMode = () => (
    <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',backgroundColor:c.bg}}>
      {/* Back arrow header */}
      <div style={{display:'flex',alignItems:'center',padding:'14px 16px 8px',flexShrink:0}}>
        <button onMouseDown={MD} onClick={()=>setMode('calculator')}
          style={{background:'none',border:'none',cursor:'pointer',color:c.text,
            padding:'0 12px 0 0',display:'flex',alignItems:'center',flexShrink:0}}>
          <ChevronLeft size={26}/>
        </button>
        <span style={{fontSize:'1.25rem',fontWeight:700,color:c.text,fontFamily:FONT}}>Financial Tools</span>
        <div style={{flex:1}}/>
        <div style={{position:'relative'}}>
          <button onMouseDown={MD} onClick={()=>setShowSettings(s=>!s)} style={iconBtn(showSettings)}><Settings size={20}/></button>
          {showSettings && (
            <div style={{position:'absolute',top:'100%',right:0,zIndex:200,marginTop:'4px',minWidth:'220px'}}>
              {renderSettings()}
            </div>
          )}
        </div>
      </div>
      <div style={{height:'1px',backgroundColor:c.border,flexShrink:0}}/>
      <div style={{display:'flex',gap:'4px',padding:'8px 16px',overflowX:'auto',flexShrink:0,scrollbarWidth:'none'}}>
        {finTabs.map(({id,label,icon:Icon})=><button key={id} onClick={()=>setFinancialMode(id)} style={finChip(financialMode===id)}><Icon size={13}/>{label}</button>)}
      </div>
      <div style={{height:'1px',backgroundColor:c.border,flexShrink:0}}/>
      <div style={{flex:1,overflowY:'auto'}}>
        {finTabs.find(t=>t.id===financialMode)?.render()}
      </div>
    </div>
  );

  // ── Root ──
  return (
    <div style={{height:'100dvh',backgroundColor:isCapacitor()?c.bg:'#111',display:'flex',justifyContent:'center',alignItems:'center'}}>
      <div style={{
        height:'100dvh', width:'100%', 
        
       /* FIXED: Cap the desktop/tablet width at 850px so it doesn't stretch forever! */
        maxWidth: isCapacitor() ? '100%' : (isTablet ? '850px' : '430px'), 
        
        backgroundColor:c.bg, display:'flex', flexDirection:'column', fontFamily:FONT, overflow:'hidden',
        paddingTop:'env(safe-area-inset-top)', 
        paddingBottom: !isFullyPro && isCapacitor() ? 'calc(env(safe-area-inset-bottom, 0px))' : 'env(safe-area-inset-bottom, 0px)',
        transition: 'max-width 0.35s cubic-bezier(0.4,0,0.2,1)'
      }}>
        <div style={{
          flex:1,
          display:'flex',
          /* STRICTLY STACKED: No more side-by-side split screen */
          flexDirection: 'column',
          overflow:'hidden',position:'relative',
          backgroundColor:c.bg,
        }}>
          
          {/* THE LEFT PANEL HAS BEEN COMPLETELY DELETED FROM HERE! */}

          {/* Lightning-fast Micro-Fade Routing */}
          <AnimatePresence mode="wait">
            {mode === 'calculator' && (
              <motion.div key="calc" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.1 }} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {renderCalcMode()}
              </motion.div>
            )}
            {mode === 'converter' && (
              <motion.div key="conv" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.1 }} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {renderConverterMode()}
              </motion.div>
            )}
            {mode === 'financial' && (
              <motion.div key="fin" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.1 }} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {renderFinancialMode()}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <style>{`
          *{-webkit-tap-highlight-color:transparent;box-sizing:border-box;}
          input::-webkit-outer-spin-button,input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}
          input[type=number]{-moz-appearance:textfield;}
          ::-webkit-scrollbar{display:none;}
          @keyframes cvBlink{0%,100%{opacity:1}50%{opacity:0}}
          @keyframes toastIn{from{opacity:0;transform:translateX(-50%) translateY(10px) scale(0.96)}to{opacity:1;transform:translateX(-50%) translateY(0) scale(1)}}
          button{transition:opacity 0.12s cubic-bezier(0.4,0,0.2,1),transform 0.12s cubic-bezier(0.4,0,0.2,1);}
          button:active{opacity:0.6!important;transform:scale(0.93)!important;}
          .calc-sel-d option{background:#1a1a1a;color:#fff;} .calc-sel-l option{background:#f2f2f7;color:#000;}
        `}</style>
        
        <RemoveAdsModal
          visible={showRemoveAds}
          onClose={() => setShowRemoveAds(false)}
          price={price}
          purchasing={purchasing}
          restoring={restoring}
          error={iapError}
          onPurchase={purchaseRemoveAds}
          onRestore={restorePurchases}
          theme={theme}
        />
        
        {showPremiumPaywall && (
          <PremiumPaywall 
            user={user} 
            onLoginSuccess={onLoginSuccess} 
            freeUsesLeft={freeUsesLeft}
            targetFeature={premiumTarget}
            onClose={() => setShowPremiumPaywall(false)} 
            onConsumeFreeUse={() => { 
              consumeFreeUse(premiumTarget); // Pass the target here!
              if (premiumTarget === 'history') { setMode('calculator'); setShowHistory(true); }
              else if (premiumTarget === 'converter') { setMode('converter'); }
              else if (premiumTarget === 'financial') { setMode('financial'); }
            }}
          />
        )}

      </div>
    </div>
  );
}; // End of CalculatorApp component

// ── THE GATEKEEPER SYSTEM ──

const AndroidPlayStoreBlocker = () => (
  <div style={{ height: '100dvh', backgroundColor: '#111', display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center', padding: '24px', fontFamily: FONT }}>
    <div>
      <svg style={{ display: 'block', margin: '0 auto 16px', fill: '#3DDC84' }} width="64" height="64" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4483-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993.0001.5511-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993 0 .5511-.4482.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 00-.1521-.5676.4161.4161 0 00-.5676.1521l-2.0223 3.503C15.5902 8.244 13.8533 7.85 12 7.85c-1.8533 0-3.5902.394-5.1367 1.1004L4.841 5.4475a.416.416 0 00-.5676-.1521.416.416 0 00-.1521.5676l1.9973 3.4592C2.6889 11.1867.3432 14.6589 0 18.761h24c-.3435-4.1021-2.6892-7.5743-6.1185-9.4396"/></svg>
      <h2 style={{ fontSize: '1.4rem', color: '#fff', fontWeight: 600, marginBottom: '12px' }}>Download the App</h2>
      <p style={{ color: '#9E9E9E', fontSize: '0.95rem', lineHeight: 1.5, maxWidth: '320px', margin: '0 auto' }}>
        This web version is optimized exclusively for iOS devices. Android users, please download our native app from the Play Store for the best experience.
      </p>
      <a href="https://play.google.com/store/apps/details?id=com.mbsstudios.calc360" style={{ display: 'block', textDecoration: 'none', marginTop: '32px', padding: '14px 24px', borderRadius: '50px', border: 'none', background: '#34C759', color: '#000', fontFamily: FONT, fontWeight: 700, width: '100%', boxSizing: 'border-box' }}>
        Get it on Google Play
      </a>
    </div>
  </div>
);

const PremiumPaywall = ({ user, onLoginSuccess, freeUsesLeft, onConsumeFreeUse, onClose, targetFeature }) => {
  const features = [
    { icon: "🕒", text: "Unlimited Calculation History" },
    { icon: "📊", text: "GST, EMI & SIP Tools" },
    { icon: "🔄", text: "Live Currency & Unit Converter" },
    { icon: "✨", text: "Ad-Free Premium Experience" }
  ];

  let targetName = "Advanced Features";
  if (targetFeature === 'history') targetName = "Calculation History";
  if (targetFeature === 'converter') targetName = "Unit & Currency Converter";
  if (targetFeature === 'financial') targetName = "Financial Tools";

  const handleLogin = async (redirectToCheckout = false) => {
    try {
      const googleProvider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, googleProvider);
      onLoginSuccess({ uid: result.user.uid });
      if (redirectToCheckout) { window.location.href = `https://pages.razorpay.com/mbsstudios?user_id=${result.user.uid}`; }
    } catch (error) {
      console.error("Login failed:", error.message);
      alert("Failed to log in. Please try again.");
    }
  };

  const handleCheckout = () => {
    if (user) { window.location.href = `https://pages.razorpay.com/mbsstudios?user_id=${user.uid}`; } 
    else { handleLogin(true); }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100dvh', backgroundColor: 'rgba(17, 17, 17, 0.98)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 'env(safe-area-inset-top) 20px env(safe-area-inset-bottom) 20px', fontFamily: FONT, boxSizing: 'border-box', overflow: 'hidden', zIndex: 9999 }}>
      <div style={{ maxWidth: '380px', width: '100%', display: 'flex', flexDirection: 'column', maxHeight: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '10px' }}>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#9E9E9E', fontSize: '1rem', cursor: 'pointer', padding: '8px 0', fontFamily: FONT }}>← Back to Free Calculator</button>
        </div>
        <div style={{ flexShrink: 0 }}>
          <div style={{ background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', display: 'inline-block', padding: '4px 12px', borderRadius: '50px', fontSize: '0.7rem', fontWeight: 800, color: '#000', marginBottom: '12px', textTransform: 'uppercase' }}>Premium Feature</div>
          <h1 style={{ fontSize: 'clamp(1.6rem, 5vw, 2rem)', color: '#fff', fontWeight: 700, marginBottom: '8px', marginTop: 0 }}>{targetName}</h1>
          {freeUsesLeft > 0 ? (
            <p style={{ color: '#34C759', marginBottom: '12px', fontSize: '0.95rem', lineHeight: '1.4', fontWeight: 500 }}>You are about to use 1 Free Pass to experience the {targetName} feature.</p>
          ) : (
            <p style={{ color: '#FF453A', marginBottom: '12px', fontSize: '0.95rem', lineHeight: '1.4', fontWeight: 500 }}>You have 0 Free Passes remaining.</p>
          )}
          <p style={{ color: '#9E9E9E', marginBottom: '16px', fontSize: '0.85rem', lineHeight: '1.4' }}>Unlock Lifetime Pro to get unlimited access to this, plus all the features below:</p>
        </div>
        <div style={{ background: '#1C1C1E', borderRadius: '20px', padding: '12px 16px', marginBottom: '16px', border: '1px solid #2C2C2E', textAlign: 'left', flexShrink: 1, overflowY: 'auto' }}>
          {features.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: i === features.length - 1 ? 'none' : '1px solid #2C2C2E' }}>
              <span style={{ fontSize: '1.1rem' }}>{f.icon}</span><span style={{ color: '#fff', fontWeight: 500, fontSize: '0.85rem' }}>{f.text}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px', flexShrink: 0 }}>
          {user ? (
            <>
              <p style={{ color: '#34C759', fontSize: '0.8rem', fontWeight: 600, margin: '0 0 2px 0' }}>✓ Signed in</p>
              <button onClick={handleCheckout} style={{ padding: '14px', borderRadius: '16px', background: '#fff', color: '#000', fontWeight: 'bold', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>Pay ₹99 Securely</button>
              <button onClick={() => { signOut(auth); window.location.reload(); }} style={{ padding: '10px', background: 'transparent', color: '#666', border: 'none', cursor: 'pointer', fontSize: '0.85rem' }}>Sign out / Switch Account</button>
            </>
          ) : (
            <>
              <button onClick={handleCheckout} style={{ padding: '14px', borderRadius: '16px', background: '#fff', color: '#000', fontWeight: 'bold', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>Unlock Lifetime Pro (₹99)</button>
              <button onClick={() => handleLogin(false)} style={{ padding: '12px', borderRadius: '16px', background: '#2C2C2E', color: '#fff', fontWeight: 600, border: 'none', cursor: 'pointer', fontSize: '0.9rem' }}>Sign in (Already Paid?)</button>
            </>
          )}
          {freeUsesLeft > 0 && (
            <button onClick={() => { onConsumeFreeUse(); onClose(); }} style={{ padding: '12px', borderRadius: '16px', background: 'transparent', color: '#34C759', fontWeight: 600, border: '2px solid #34C759', cursor: 'pointer', fontSize: '0.9rem', marginTop: '4px' }}>Use 1 Free Pass ({freeUsesLeft} Remaining)</button>
          )}
        </div>
        <div style={{ flexShrink: 0, paddingBottom: '10px', marginTop: '8px' }}>
          <a href="/privacy.html" style={{ color: '#666', fontSize: '0.8rem', textDecoration: 'underline' }}>Privacy Policy</a>
        </div>
      </div>
    </div>
  );
};

const AppGatekeeper = () => {
  const [user, setUser] = useState(null); 
  const [isProWeb, setIsProWeb] = useState(false);
  const [isLoading, setIsLoading] = useState(!isCapacitor());
  
  // --- NEW CODE: THE PASS COUNTER ---
  // We check localStorage first. If they are a brand new user, they get 3.
  const [freeUsesLeft, setFreeUsesLeft] = useState(() => {
    const stored = localStorage.getItem('calc360_free_uses');
    return stored !== null ? parseInt(stored, 10) : 3; 
  });

  const [activePasses, setActivePasses] = useState(() => {
    const stored = localStorage.getItem('calc360_active_passes');
    return stored ? JSON.parse(stored) : {};
  });

  // Function to deduct 1 pass and save it to localStorage and start a 2-minute timer for that feature
  const consumeFreeUse = (feature) => {
    if (freeUsesLeft > 0) {
      const newAmount = freeUsesLeft - 1;
      setFreeUsesLeft(newAmount);
      localStorage.setItem('calc360_free_uses', newAmount.toString());
      // Set expiration to 2 minutes from now (120,000 ms)
      const newPasses = { ...activePasses, [feature]: Date.now() + 120000 };
      setActivePasses(newPasses);
      localStorage.setItem('calc360_active_passes', JSON.stringify(newPasses));
      return true;
    }
    return false;
  };

  const PUBLIC_KEY = "goog_tIMIvflHKMQScawcideHUTroaYE"; 

  // 1. Check Firebase Auth
  useEffect(() => {
    if (isCapacitor()) return;
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) setUser({ uid: firebaseUser.uid });
      else setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // 2. Check RevenueCat Status
  useEffect(() => {
    // Only run if we have a user and we haven't confirmed Pro status yet
    if (isCapacitor() || !user) return;

    const checkStatus = async () => {
      // Start loading specifically for the RevenueCat check
      setIsLoading(true); 
      try {
        const response = await fetch(`https://api.revenuecat.com/v1/subscribers/${user.uid}`, {
          headers: {
            'Authorization': `Bearer ${PUBLIC_KEY}`,
            'Content-Type': 'application/json',
            'X-Platform': 'stripe' 
          }
        });

        if (response.status === 404) {
          setIsProWeb(false);
        } else {
          const data = await response.json();
          if (data.subscriber?.entitlements?.remove_ads) {
            setIsProWeb(true);
          }
        }
      } catch (e) {
        console.error("Pro Check Failed:", e);
      } finally {
        // Now we are officially done loading everything
        setIsLoading(false); 
      }
    };

    checkStatus();
  }, [user]); // Re-runs ONLY when the user object is updated
  
    
  if (isLoading) return <div style={{ height: '100dvh', backgroundColor: '#000' }} />;
  if (isAndroidWeb()) return <AndroidPlayStoreBlocker />;
  
  // ALWAYS return the app, passing down the lock status and pass functions!
  return (
    <CalculatorApp 
      isProWeb={isProWeb} 
      freeUsesLeft={freeUsesLeft} 
      consumeFreeUse={consumeFreeUse} 
      user={user} 
      onLoginSuccess={setUser} 
      activePasses={activePasses} 
    />
  );
};
export default AppGatekeeper;